<template>
  <public-layout>
    <div class="co-page">

      <!-- Banner -->
      <div class="co-banner">
        <img v-if="company.banner" :src="company.banner" alt="" class="co-banner__img" @error="e => e.target.style.display='none'" />
        <div class="co-banner__dots"></div>
        <button v-if="isOwner" class="co-banner__edit" data-toggle="modal" data-target="#update-banner" title="Update banner">
          <i class="feather-edit"></i>
        </button>
      </div>

      <div class="sc-container">

        <!-- Header Card -->
        <div class="co-header">
          <div class="co-logo-wrap">
            <img class="co-logo" :src="logoSrc" :alt="company.name || 'Company'" @error="onLogoError" />
          </div>
          <div class="co-info">
            <h1 class="co-name">
              {{ company.name || '...' }}
              <svg v-if="company.verified" class="co-verified" width="18" height="18" fill="none" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="12" fill="#0891b2"/>
                <path d="M7 12l3.5 3.5L17 8" stroke="#fff" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </h1>
            <p v-if="company.industry" class="co-tagline">{{ company.industry.name }}</p>
            <p v-if="companyLocation" class="co-location">
              <svg width="14" height="14" fill="none" viewBox="0 0 24 24"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5A2.5 2.5 0 019.5 9 2.5 2.5 0 0112 6.5 2.5 2.5 0 0114.5 9 2.5 2.5 0 0112 11.5z" fill="currentColor"/></svg>
              {{ companyLocation }}
            </p>
          </div>
          <div class="co-actions">
            <!-- Visit Website -->
            <a
              v-if="company.website"
              :href="company.website"
              target="_blank"
              rel="noopener"
              class="co-btn co-btn--outline"
            >
              <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 010 20M12 2a15.3 15.3 0 000 20" stroke-linecap="round"/></svg>
              Visit Website
            </a>
            <button
              v-else-if="isOwner"
              class="co-btn co-btn--outline co-btn--muted"
              data-toggle="modal"
              data-target="#update-company"
              title="Add your website URL in Edit"
            >
              <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
              Add Website
            </button>

            <template v-if="isOwner">
              <button class="co-btn co-btn--outline" data-toggle="modal" data-target="#update-company">
                <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                Edit
              </button>
              <button class="co-btn co-btn--primary" data-toggle="modal" data-target="#project-modal">
                <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                Add Listing
              </button>
            </template>
            <template v-else>
              <button
                v-if="check_follower == 0"
                class="co-btn co-btn--primary"
                :disabled="loading"
                @click="followCompany(company.id)"
              >
                <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                Follow
              </button>
              <button
                v-else
                class="co-btn co-btn--outline"
                :disabled="loading"
                @click="unfollowCompany(company.id)"
              >
                <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg>
                Following
              </button>
            </template>
          </div>
        </div>

        <!-- Stats Bar -->
        <div class="co-stats">
          <div class="co-stat">
            <span class="co-stat__value">{{ projects.length }}</span>
            <span class="co-stat__label">Projects</span>
          </div>
          <div class="co-stat-divider"></div>
          <div class="co-stat">
            <span class="co-stat__value">{{ properties.length }}</span>
            <span class="co-stat__label">Properties</span>
          </div>
          <div class="co-stat-divider"></div>
          <div class="co-stat">
            <span class="co-stat__value">{{ plans.length }}</span>
            <span class="co-stat__label">Concepts</span>
          </div>
          <div class="co-stat-divider"></div>
          <div class="co-stat">
            <span class="co-stat__value">{{ total_follower }}</span>
            <span class="co-stat__label">Followers</span>
          </div>
        </div>

        <!-- Body -->
        <div class="co-body">

          <!-- Sidebar -->
          <aside class="co-aside">
            <div class="aside-card">
              <h3 class="aside-title">About</h3>
              <div v-if="company.short_description" class="aside-desc" v-html="company.short_description"></div>
              <p v-else class="aside-empty">No description yet.</p>

              <ul class="aside-meta">
                <li v-if="company.industry">
                  <svg width="15" height="15" fill="none" viewBox="0 0 24 24"><path d="M3 21h18M6 21V7l6-4 6 4v14M9 21v-5h6v5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
                  {{ company.industry.name }}
                </li>
                <li v-if="company.company_size">
                  <svg width="15" height="15" fill="none" viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
                  {{ company.company_size }} employees
                </li>
                <li v-if="companyLocation">
                  <svg width="15" height="15" fill="none" viewBox="0 0 24 24"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5A2.5 2.5 0 019.5 9 2.5 2.5 0 0112 6.5 2.5 2.5 0 0114.5 9 2.5 2.5 0 0112 11.5z" fill="currentColor"/></svg>
                  {{ companyLocation }}
                </li>
                <li v-if="company.website">
                  <svg width="15" height="15" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.8"/><path d="M2 12h20M12 2a15.3 15.3 0 010 20M12 2a15.3 15.3 0 000 20" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>
                  <a :href="company.website" target="_blank" rel="noopener" class="aside-link">{{ company.website }}</a>
                </li>
                <li v-if="company.email">
                  <svg width="15" height="15" fill="none" viewBox="0 0 24 24"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/><polyline points="22,6 12,13 2,6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
                  {{ company.email }}
                </li>
                <li v-if="company.phone_number">
                  <svg width="15" height="15" fill="none" viewBox="0 0 24 24"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 10.8 19.79 19.79 0 01.07 2.18 2 2 0 012.06 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
                  {{ company.phone_number }}<span v-if="company.phone_number2">, {{ company.phone_number2 }}</span>
                </li>
              </ul>
            </div>

            <!-- Followers widget -->
            <div v-if="followers.length > 0" class="aside-card">
              <h3 class="aside-title">Followers</h3>
              <div v-for="data in followers.slice(0,6)" :key="data.id" class="follower-item">
                <router-link :to="'/sc/profile/'+data.user.slug" class="follower-avatar">
                  <img v-if="data.user.avatar" :src="image_url+'users/'+data.user.avatar" :alt="data.user.name" @error="onImgError" />
                  <img v-else src="/assets/avatar.png" :alt="data.user.name" />
                </router-link>
                <router-link :to="'/sc/profile/'+data.user.slug" class="follower-info">
                  <span class="follower-name">{{ data.user.name }}</span>
                  <span class="follower-sub">{{ data.user.profession || (data.user.country ? data.user.country.name : '') }}</span>
                </router-link>
              </div>
            </div>
          </aside>

          <!-- Main -->
          <main class="co-main">
            <!-- Filter Tabs -->
            <div class="filter-tabs">
              <button :class="['tab-btn', activeTab === 'projects' && 'tab-btn--active']" @click="activeTab = 'projects'">
                Projects <span class="tab-count">{{ projects.length }}</span>
              </button>
              <button :class="['tab-btn', activeTab === 'properties' && 'tab-btn--active']" @click="activeTab = 'properties'">
                Properties <span class="tab-count">{{ properties.length }}</span>
              </button>
              <button :class="['tab-btn', activeTab === 'plans' && 'tab-btn--active']" @click="activeTab = 'plans'">
                Concepts <span class="tab-count">{{ plans.length }}</span>
              </button>
              <button :class="['tab-btn', activeTab === 'followers' && 'tab-btn--active']" @click="activeTab = 'followers'">
                Followers <span class="tab-count">{{ total_follower }}</span>
              </button>
              <button v-if="connections.length > 0" :class="['tab-btn', activeTab === 'connections' && 'tab-btn--active']" @click="activeTab = 'connections'">
                Connections <span class="tab-count">{{ connections.length }}</span>
              </button>
            </div>

            <!-- Projects Tab -->
            <div v-if="activeTab === 'projects'">
              <div v-if="!projects.length" class="co-empty">
                <svg width="48" height="48" fill="none" viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" stroke-width="1.5"/><path d="M3 9h18M9 21V9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
                <p>No projects yet.</p>
              </div>
              <div v-else>
                <div v-for="(data, index) in projects" :key="data.id" class="feed-card">
                  <div class="feed-card__actions" v-if="data.user.id == id || data.user.owner_id == id">
                    <div class="dropdown">
                      <a href="#" role="button" data-toggle="dropdown" class="feed-card__menu">
                        <i class="feather-more-horizontal"></i>
                      </a>
                      <div class="dropdown-menu dropdown-menu-right">
                        <a class="dropdown-item" style="cursor:pointer" @click="confirmDelete('project', data.id, data.name)">Delete</a>
                      </div>
                    </div>
                  </div>
                  <div class="feed-card__header">
                    <img :src="data.user.avatar ? image_url+'users/'+data.user.avatar : '/assets/avatar.png'" class="feed-card__avatar" :alt="data.user.name" @error="onImgError" />
                    <div>
                      <div class="feed-card__author">{{ data.user ? data.user.name : '' }}</div>
                      <div class="feed-card__meta">{{ data.user.state ? data.user.state.name+',' : '' }} {{ data.user.country ? data.user.country.name : '' }}</div>
                    </div>
                    <span class="feed-card__time">{{ formatDate(data.created_at) }}</span>
                  </div>
                  <div class="feed-card__body">
                    <router-link :to="'/project/'+data.slug" class="feed-card__title">{{ data.name }}</router-link>
                    <p class="feed-card__location">
                      <i class="feather-map-pin"></i>
                      {{ data.state ? data.state.name+',' : '' }} {{ data.country ? data.country.name : '' }}
                    </p>
                    <div class="feed-card__desc">
                      <span v-if="data.description.length > 250">
                        <span v-if="!readMore[data.id]"><span v-html="data.description.substring(0,250)+'....'"></span> <a @click="showMore(data.id)" class="feed-card__readmore">view more</a></span>
                        <span v-else><span v-html="data.description"></span> <a @click="showLess(data.id)" class="feed-card__readmore">view less</a></span>
                      </span>
                      <span v-else v-html="data.description"></span>
                    </div>
                    <div v-if="data.images.length" class="feed-card__img-wrap">
                      <router-link :to="'/project/'+data.slug">
                        <img :src="data.images[0].path" class="feed-card__img" alt="" @error="onImgError" />
                      </router-link>
                    </div>
                    <a style="cursor:pointer" @click="viewTeam(index)">
                      <div class="feed-card__connections">
                        <div class="connection-avatars">
                          <img v-for="connect in data.tags.slice(0,6)" :key="connect.id" :src="connect.user.avatar ? image_url+'users/'+connect.user.avatar : '/assets/avatar.png'" :alt="connect.user.name" @error="onImgError" />
                        </div>
                        <span>{{ data.tags.length }} connections</span>
                      </div>
                    </a>
                  </div>
                  <div class="feed-card__footer">
                    <button class="feed-card__reaction" @click="likeTimeline(index, data.id)">
                      <i class="feather-thumbs-up"></i>
                    </button>
                    <button class="feed-card__reaction" @click="viewLikes(index)">{{ data.likes.length }} likes</button>
                    <button v-if="data.comments.length > 0" class="feed-card__reaction" @click="viewComments(index)">
                      <i class="feather-message-square"></i> {{ data.comments.length }}
                    </button>
                    <span v-else class="feed-card__reaction"><i class="feather-message-square"></i> 0</span>
                  </div>
                  <div v-for="com in data.comments.slice(0,3)" :key="com.id" class="feed-card__comment">
                    <img :src="com.user.avatar ? image_url+'users/'+com.user.avatar : '/assets/avatar.png'" class="feed-card__comment-avatar" :alt="com.user.name" @error="onImgError" />
                    <div class="feed-card__comment-body">
                      <span class="feed-card__comment-author">{{ com.user.name }}</span>
                      <span class="feed-card__comment-time">{{ formatDate(com.created_at) }}</span>
                      <p>{{ com.comment }}</p>
                    </div>
                  </div>
                  <div class="feed-card__compose">
                    <textarea placeholder="Add a comment..." rows="1" :id="'submitCommentButton'+data.id" v-model="comment.description[index]" @keydown.enter.exact.prevent @keyup.enter.exact="submitComment($event, data.id)" @keydown.enter.shift.exact="newline"></textarea>
                    <button type="button" @click="sendSubmitComment(data.id)"><i class="feather-send"></i></button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Properties Tab -->
            <div v-if="activeTab === 'properties'">
              <div v-if="!properties.length" class="co-empty">
                <svg width="48" height="48" fill="none" viewBox="0 0 24 24"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M9 22V12h6v10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                <p>No properties yet.</p>
              </div>
              <div v-else>
                <div v-for="(data, index) in properties" :key="data.id" class="feed-card">
                  <div class="feed-card__actions" v-if="data.user.id == id || data.user.owner_id == id">
                    <div class="dropdown">
                      <a href="#" role="button" data-toggle="dropdown" class="feed-card__menu">
                        <i class="feather-more-horizontal"></i>
                      </a>
                      <div class="dropdown-menu dropdown-menu-right">
                        <a class="dropdown-item" style="cursor:pointer" @click="confirmDelete('property', data.id, data.name)">Delete</a>
                      </div>
                    </div>
                  </div>
                  <div class="feed-card__header">
                    <img :src="data.user.avatar ? image_url+'users/'+data.user.avatar : '/assets/avatar.png'" class="feed-card__avatar" :alt="data.user.name" @error="onImgError" />
                    <div>
                      <div class="feed-card__author">{{ data.user ? data.user.name : '' }}</div>
                      <div class="feed-card__meta">{{ data.user.state ? data.user.state.name+',' : '' }} {{ data.user.country ? data.user.country.name : '' }}</div>
                    </div>
                    <span class="feed-card__time">{{ formatDate(data.created_at) }}</span>
                  </div>
                  <div class="feed-card__body">
                    <router-link :to="'/post/'+data.slug" class="feed-card__title">{{ data.name }}</router-link>
                    <p class="feed-card__location">
                      <i class="feather-map-pin"></i>
                      {{ data.state ? data.state.name+',' : '' }} {{ data.country ? data.country.name : '' }}
                    </p>
                    <div class="feed-card__desc">
                      <span v-if="data.description.length > 250">
                        <span v-if="!readMore[data.id]"><span v-html="data.description.substring(0,200)+'....'"></span> <a @click="showMore(data.id)" class="feed-card__readmore">view more</a></span>
                        <span v-else><span v-html="data.description"></span> <a @click="showLess(data.id)" class="feed-card__readmore">view less</a></span>
                      </span>
                      <span v-else v-html="data.description"></span>
                    </div>
                    <div v-if="data.images.length" class="feed-card__img-wrap">
                      <router-link :to="'/post/'+data.slug">
                        <img :src="data.images[0].path" class="feed-card__img" alt="" @error="onImgError" />
                      </router-link>
                    </div>
                  </div>
                  <div class="feed-card__footer">
                    <button class="feed-card__reaction" @click="likePropertyTimeline(index, data.id)">
                      <i class="feather-thumbs-up"></i>
                    </button>
                    <button class="feed-card__reaction" @click="viewPropertyLikes(index)">{{ data.likes.length }} likes</button>
                    <button v-if="data.comments.length > 0" class="feed-card__reaction" @click="viewPropertyComments(index)">
                      <i class="feather-message-square"></i> {{ data.comments.length }}
                    </button>
                    <span v-else class="feed-card__reaction"><i class="feather-message-square"></i> 0</span>
                  </div>
                  <div v-for="com in data.comments.slice(0,3)" :key="com.id" class="feed-card__comment">
                    <img :src="com.user.avatar ? image_url+'users/'+com.user.avatar : '/assets/avatar.png'" class="feed-card__comment-avatar" :alt="com.user.name" @error="onImgError" />
                    <div class="feed-card__comment-body">
                      <span class="feed-card__comment-author">{{ com.user.name }}</span>
                      <span class="feed-card__comment-time">{{ formatDate(com.created_at) }}</span>
                      <p>{{ com.comment }}</p>
                    </div>
                  </div>
                  <div class="feed-card__compose">
                    <textarea placeholder="Add a comment..." rows="1" :id="'submitPropertyCommentButton'+data.id" v-model="comment.description[index]" @keydown.enter.exact.prevent @keyup.enter.exact="submitPropertyComment($event, data.id)" @keydown.enter.shift.exact="newline"></textarea>
                    <button type="button" @click="sendSubmitPropertyComment(data.id)"><i class="feather-send"></i></button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Plans Tab -->
            <div v-if="activeTab === 'plans'">
              <div v-if="!plans.length" class="co-empty">
                <svg width="48" height="48" fill="none" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><polyline points="14 2 14 8 20 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                <p>No concepts yet.</p>
              </div>
              <div v-else>
                <div v-for="(data, index) in plans" :key="data.id" class="feed-card">
                  <div class="feed-card__actions" v-if="data.user.id == id || data.user.owner_id == id">
                    <div class="dropdown">
                      <a href="#" role="button" data-toggle="dropdown" class="feed-card__menu">
                        <i class="feather-more-horizontal"></i>
                      </a>
                      <div class="dropdown-menu dropdown-menu-right">
                        <a class="dropdown-item" style="cursor:pointer" @click="confirmDelete('plan', data.id, data.title)">Delete</a>
                      </div>
                    </div>
                  </div>
                  <div class="feed-card__header">
                    <img :src="data.user.avatar ? image_url+'users/'+data.user.avatar : '/assets/avatar.png'" class="feed-card__avatar" :alt="data.user.name" @error="onImgError" />
                    <div>
                      <div class="feed-card__author">{{ data.user ? data.user.name : '' }}</div>
                      <div class="feed-card__meta">{{ data.user.state ? data.user.state.name+',' : '' }} {{ data.user.country ? data.user.country.name : '' }}</div>
                    </div>
                    <span class="feed-card__time">{{ formatDate(data.created_at) }}</span>
                  </div>
                  <div class="feed-card__body">
                    <router-link :to="'/plan/'+data.slug" class="feed-card__title">{{ data.name }}</router-link>
                    <div class="feed-card__desc">
                      <span v-if="data.description.length > 250">
                        <span v-if="!readMore[data.id]"><span v-html="data.description.substring(0,200)+'....'"></span> <a @click="showMore(data.id)" class="feed-card__readmore">view more</a></span>
                        <span v-else><span v-html="data.description"></span> <a @click="showLess(data.id)" class="feed-card__readmore">view less</a></span>
                      </span>
                      <span v-else v-html="data.description"></span>
                    </div>
                    <div v-if="data.images.length" class="feed-card__img-wrap">
                      <router-link :to="'/plan/'+data.slug">
                        <img :src="data.images[0].path" class="feed-card__img" alt="" @error="onImgError" />
                      </router-link>
                    </div>
                  </div>
                  <div class="feed-card__footer">
                    <button class="feed-card__reaction" @click="likePlanTimeline(index, data.id)">
                      <i class="feather-thumbs-up"></i>
                    </button>
                    <button class="feed-card__reaction" @click="viewPlanLikes(index)">{{ data.likes.length }} likes</button>
                    <button v-if="data.comments.length > 0" class="feed-card__reaction" @click="viewPlanComments(index)">
                      <i class="feather-message-square"></i> {{ data.comments.length }}
                    </button>
                    <span v-else class="feed-card__reaction"><i class="feather-message-square"></i> 0</span>
                  </div>
                  <div v-for="com in data.comments.slice(0,3)" :key="com.id" class="feed-card__comment">
                    <img :src="com.user.avatar ? image_url+'users/'+com.user.avatar : '/assets/avatar.png'" class="feed-card__comment-avatar" :alt="com.user.name" @error="onImgError" />
                    <div class="feed-card__comment-body">
                      <span class="feed-card__comment-author">{{ com.user.name }}</span>
                      <span class="feed-card__comment-time">{{ formatDate(com.created_at) }}</span>
                      <p>{{ com.comment }}</p>
                    </div>
                  </div>
                  <div class="feed-card__compose">
                    <textarea placeholder="Add a comment..." rows="1" :id="'submitPlanCommentButton'+data.id" v-model="comment.description[index]" @keydown.enter.exact.prevent @keyup.enter.exact="submitPlanComment($event, data.id)" @keydown.enter.shift.exact="newline"></textarea>
                    <button type="button" @click="sendSubmitPlanComment(data.id)"><i class="feather-send"></i></button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Followers Tab -->
            <div v-if="activeTab === 'followers'">
              <div v-if="!followers.length" class="co-empty">
                <svg width="48" height="48" fill="none" viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                <p>No followers yet.</p>
              </div>
              <div v-else class="people-grid">
                <div v-for="data in followers" :key="data.id" class="people-card">
                  <router-link :to="'/sc/profile/'+data.user.slug">
                    <img :src="data.user.avatar ? image_url+'users/'+data.user.avatar : '/assets/avatar.png'" class="people-card__avatar" :alt="data.user.name" @error="onImgError" />
                  </router-link>
                  <div class="people-card__info">
                    <router-link :to="'/sc/profile/'+data.user.slug" class="people-card__name">{{ data.user.name }}</router-link>
                    <p class="people-card__sub">{{ data.user.profession || (data.user.country ? data.user.country.name : '') }}</p>
                  </div>
                  <button class="people-card__follow" @click="follow(data.user.id)">Follow</button>
                </div>
              </div>
            </div>

            <!-- Connections Tab -->
            <div v-if="activeTab === 'connections'">
              <div v-if="!connections.length" class="co-empty">
                <p>No connections yet.</p>
              </div>
              <div v-else class="connections-list">
                <div v-for="(data, index) in connections" :key="index" class="connection-card">
                  <div v-if="data.project">
                    <div class="connection-card__top">
                      <div class="connection-card__info">
                        <router-link :to="'/project/'+data.project.slug" class="connection-card__name">{{ data.project.name }}</router-link>
                        <p class="connection-card__location">{{ data.project.state ? data.project.state.name+',' : '' }} {{ data.project.country ? data.project.country.name : '' }}</p>
                        <p class="connection-card__dates">{{ formatDate2(data.project.project_start_date) }} – {{ data.project.status == 1 ? formatDate2(data.project.project_end_date) : 'Present' }}</p>
                      </div>
                      <img v-if="data.project.images.length" :src="data.project.images[0].path" class="connection-card__thumb" alt="" @error="onImgError" />
                    </div>
                    <p v-if="data.project.description" class="connection-card__desc" v-html="data.project.description.substring(0,200)+'...'"></p>
                    <a style="cursor:pointer" @click="viewTeam2(index)">
                      <div class="feed-card__connections">
                        <div class="connection-avatars">
                          <img v-for="connect in data.project.tags.slice(0,6)" :key="connect.id" :src="connect.user.avatar ? image_url+'users/'+connect.user.avatar : initAvatar(connect.user.name)" :alt="connect.user.name" @error="e => e.target.src = initAvatar(connect.user.name)" />
                        </div>
                        <span>{{ data.project.tags.length }} connections</span>
                      </div>
                    </a>
                  </div>
                </div>
              </div>
            </div>

          </main>
        </div>
      </div>





        <!-- update Modal -->
      <div class="modal fade" id="update-company" tabindex="-1" role="dialog" aria-hidden="true">
        <div class="modal-dialog uc-dialog" role="document">
          <div class="modal-content uc-content">

            <!-- Header -->
            <div class="uc-header">
              <h2 class="uc-title">Edit Company</h2>
              <button ref="ucCloseBtn" type="button" class="uc-close" data-dismiss="modal" aria-label="Close">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            <!-- Body -->
            <div class="uc-body">
              <form class="uc-form" novalidate @submit.prevent>

                <!-- Name + Industry -->
                <div class="uc-row">
                  <div class="uc-field">
                    <label class="uc-label">Company Name <span class="uc-req">*</span></label>
                    <input type="text" class="uc-input" v-model="company.name" placeholder="Company name" />
                  </div>
                  <div class="uc-field">
                    <label class="uc-label">Industry</label>
                    <select class="uc-input" v-model="company.industry_id">
                      <option value="">-- select industry --</option>
                      <option v-for="d in industries" :key="d.id" :value="d.id">{{ d.name }}</option>
                    </select>
                  </div>
                </div>

                <!-- Email + Website -->
                <div class="uc-row">
                  <div class="uc-field">
                    <label class="uc-label">Contact Email <span class="uc-req">*</span></label>
                    <input type="email" class="uc-input" v-model="company.email" placeholder="company@example.com" />
                  </div>
                  <div class="uc-field">
                    <label class="uc-label">Website</label>
                    <input type="url" class="uc-input" v-model="company.website" placeholder="https://…" />
                  </div>
                </div>

                <!-- Phone 1 + Phone 2 -->
                <div class="uc-row">
                  <div class="uc-field">
                    <label class="uc-label">Phone Number <span class="uc-req">*</span></label>
                    <input type="tel" class="uc-input" v-model="company.phone_number" placeholder="+234…" />
                  </div>
                  <div class="uc-field">
                    <label class="uc-label">Phone Number 2</label>
                    <input type="tel" class="uc-input" v-model="company.phone_number2" placeholder="+234…" />
                  </div>
                </div>

                <!-- Company Size -->
                <div class="uc-field">
                  <label class="uc-label">Company Size</label>
                  <select class="uc-input" v-model="company.company_size">
                    <option value="">-- select size --</option>
                    <option value="1-10">1–10 employees</option>
                    <option value="11-25">11–25 employees</option>
                    <option value="25-50">25–50 employees</option>
                    <option value="50-100">50–100 employees</option>
                    <option value="100-500">100–500 employees</option>
                    <option value="500-1000">500–1000 employees</option>
                    <option value="1000-10000">1,000–10,000 employees</option>
                    <option value="10000-50000">10,000–50,000 employees</option>
                  </select>
                </div>

                <!-- Description -->
                <div class="uc-field">
                  <label class="uc-label">Description</label>
                  <QuillEditor id="company_editor" v-model:content="company.short_description" contentType="html" :toolbar="customToolbar" class="uc-quill" />
                </div>

                <!-- Location -->
                <p class="uc-section-label">Location</p>
                <div class="uc-row uc-row--3">
                  <div class="uc-field">
                    <label class="uc-label">Country</label>
                    <select class="uc-input" v-model="company.country_id" @change="fetchStates()">
                      <option value="">Country</option>
                      <option v-for="d in country" :key="d.id" :value="d.id">{{ d.name }}</option>
                    </select>
                  </div>
                  <div class="uc-field">
                    <label class="uc-label">State</label>
                    <select class="uc-input" v-model="company.state_id" @change="fetchCities()">
                      <option value="">State</option>
                      <option v-for="d in state" :key="d.id" :value="d.id">{{ d.name }}</option>
                    </select>
                  </div>
                  <div class="uc-field">
                    <label class="uc-label">City</label>
                    <select class="uc-input" v-model="company.city_id">
                      <option value="">City</option>
                      <option v-for="d in city" :key="d.id" :value="d.id">{{ d.name }}</option>
                    </select>
                  </div>
                </div>

                <!-- Address -->
                <div class="uc-field">
                  <label class="uc-label">Address</label>
                  <input type="text" class="uc-input" v-model="company.address" placeholder="Street address…" />
                </div>

                <!-- Logo -->
                <div class="uc-field">
                  <label class="uc-label">Company Logo</label>
                  <div class="uc-logo-row">
                    <img
                      :src="editLogoPreview || (company.logo ? image_url + 'users/' + company.logo : '/assets/avatar.png')"
                      class="uc-logo-preview"
                      @error="$event.target.src='/assets/avatar.png'"
                      alt="Logo"
                    />
                    <div class="uc-logo-actions">
                      <label class="uc-upload-btn" for="edit-logo-input">
                        {{ editLogoPreview ? 'Change' : 'Upload Logo' }}
                      </label>
                      <input id="edit-logo-input" type="file" accept="image/*" class="uc-file-hidden" @change="onEditLogoChange" />
                      <p class="uc-hint">PNG or JPG, max 2 MB</p>
                    </div>
                  </div>
                </div>

                <input type="hidden" v-model="company.id" />
              </form>
            </div>

            <!-- Footer -->
            <div class="uc-footer">
              <button type="button" class="uc-btn uc-btn--outline" data-dismiss="modal">Cancel</button>
              <button type="button" class="uc-btn uc-btn--primary" :disabled="loading" @click="updateCompany()">
                <span v-if="loading" class="uc-spinner"></span>
                <span v-else>Save Changes</span>
              </button>
            </div>

          </div>
        </div>
      </div>



      <!-- connections Modal -->
      <div v-if="currentData.project" class="modal fade" id="team-view" tabindex="-1" role="dialog" aria-labelledby="exampleModalLabel" aria-hidden="true">
        <div class="modal-dialog" role="document">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title" id="exampleModalLabel">{{currentData.project.name}}</h5>
              <button type="button" class="close" data-dismiss="modal" aria-label="Close">
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div class="modal-body">
              <div v-for="data in currentData.project.tags" :key="data.id" class="p-3 d-flex align-items-center border-bottom osahan-post-header">
                <div class="dropdown-list-image mr-3">
                  <a :href="'/sc/profile/'+data.user.slug">
                  <img v-if="data.user.avatar" class="rounded-circle" :src="image_url+'users/'+data.user.avatar" style="max-height: 150px; max-width: 150px" alt="">
                  <img v-else class="rounded-circle" src="/assets/avatar.png" style="max-height: 150px; max-width: 150px" alt="">
                  </a>
                </div>
                <div class="font-weight-bold">
                  <div class="text-truncate"><router-link :to="'/sc/profile/'+data.user.slug">{{data.user.name}}</router-link></div>
                  <div class="small text-gray-500">{{data.user.profession}} </div>
                </div>
                <span class="ml-auto"><br><button type="button" @click="follow(data.user.id)" class="btn btn-outline-primary btn-sm">Follow</button></span> 
              </div>
            </div>
            <div class="modal-footer">
              <div>
                <button type="button" class="btn btn-secondary" data-dismiss="modal">Close</button>&nbsp;
              </div>
            </div>

          </div>
        </div>
      </div>








      <!-- Confirm Delete Dialog -->
      <transition name="cd-fade">
        <div v-if="cdVisible" class="cd-backdrop" @click.self="cdCancel">
          <div class="cd-dialog">
            <div class="cd-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/></svg>
            </div>
            <h3 class="cd-title">Delete listing?</h3>
            <p class="cd-msg">
              <strong>{{ cdName }}</strong> will be permanently deleted and cannot be recovered.
            </p>
            <div class="cd-actions">
              <button type="button" class="cd-btn cd-btn--cancel" @click="cdCancel">Cancel</button>
              <button type="button" class="cd-btn cd-btn--confirm" :disabled="cdLoading" @click="cdConfirm">
                <span v-if="cdLoading" class="cd-spinner"></span>
                <span v-else>Delete</span>
              </button>
            </div>
          </div>
        </div>
      </transition>

      <!-- Modal -->
      <div class="modal fade" id="project-modal" tabindex="-1" role="dialog" aria-hidden="true">
        <div class="modal-dialog lm-dialog" role="document">
          <div class="modal-content lm-content">

            <div class="lm-header">
              <h5 class="lm-title">New Listing</h5>
              <button ref="lmCloseBtn" type="button" class="lm-close" @click="cancelForm()" data-dismiss="modal" aria-label="Close">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            <div class="lm-body">
              <!-- Post as selector -->
              <div v-if="myCompanies.length && !created" class="post-as-row mb-3">
                <span class="post-as-label">Post as:</span>
                <div class="post-as-options">
                  <button
                    :class="['post-as-opt', { 'post-as-opt--active': !postingAs }]"
                    @click="postingAs = null"
                  >
                    <img class="post-as-avatar" src="/assets/avatar.png" alt="" />
                    <span>Myself</span>
                  </button>
                  <button
                    v-for="co in myCompanies"
                    :key="co.id"
                    :class="['post-as-opt', { 'post-as-opt--active': postingAs && postingAs.id === co.id }]"
                    @click="postingAs = co"
                  >
                    <img class="post-as-avatar" :src="co.avatar ? image_url + 'users/' + co.avatar : '/assets/avatar.png'" @error="$event.target.src='/assets/avatar.png'" alt="" />
                    <span>{{ co.name }}</span>
                  </button>
                </div>
              </div>

              <!-- Success screen -->
              <div v-if="created" class="lm-success">
                <svg class="lm-success__icon" width="56" height="56" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="12" fill="#059669"/><path d="M7 12l3.5 3.5L17 8" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                <h3>Listing Added!</h3>
                <p>Your listing has been successfully published.</p>
              </div>

              <!-- Form content -->
              <template v-if="!created">

                <!-- Type selector -->
                <div class="lm-section">
                  <p class="lm-section-label">Listing Type <span class="lm-req">*</span></p>
                  <div class="lm-type-cards">
                    <button type="button" :class="['lm-type-card', { 'lm-type-card--active': type === 'project' }]" @click="type = 'project'">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/></svg>
                      <span>Project</span>
                    </button>
                    <button type="button" :class="['lm-type-card', { 'lm-type-card--active': type === 'property' }]" @click="type = 'property'">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                      <span>Property</span>
                    </button>
                    <button type="button" :class="['lm-type-card', { 'lm-type-card--active': type === 'plan' }]" @click="type = 'plan'">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
                      <span>Concept</span>
                    </button>
                  </div>
                </div>
                <!-- ── Project Form ── -->
                <template v-if="type === 'project'">
                  <div class="lm-section-header">
                    <h6 class="lm-section-title">Project Details</h6>
                  </div>
                  <div class="lm-form">

                    <div class="lm-field">
                      <label class="lm-label">Project Name <span class="lm-req">*</span></label>
                      <input type="text" class="lm-input" v-model="project.name" placeholder="Enter project name" />
                    </div>

                    <div class="lm-field">
                      <label class="lm-label">Category <span class="lm-req">*</span></label>
                      <div class="lm-category-row">
                        <select class="lm-input" v-model="project.project_interest_1" @change="fectchInterestChildren($event)">
                          <option value="" disabled selected>Main category</option>
                          <option v-for="data in interests" :key="data.id" :value="data.id">{{data.name}}</option>
                        </select>
                        <select v-if="interest_children.length" class="lm-input" v-model="project.project_interest_2" @change="fectchInterestChildren2($event)">
                          <option value="" disabled selected>Sub-category</option>
                          <option v-for="data in interest_children" :key="data.id" :value="data.id">{{data.name}}</option>
                        </select>
                        <select v-if="interest_children2.length" class="lm-input" v-model="project.project_interest_3">
                          <option value="" disabled selected>Sub-category 2</option>
                          <option v-for="data in interest_children2" :key="data.id" :value="data.id">{{data.name}}</option>
                        </select>
                      </div>
                    </div>

                    <div class="lm-field">
                      <label class="lm-label">Description <span class="lm-req">*</span></label>
                      <div class="lm-quill">
                        <QuillEditor id="project_editor" v-model:content="project.description" contentType="html" :toolbar="customToolbar" />
                      </div>
                    </div>

                    <div class="lm-row lm-row--3">
                      <div class="lm-field">
                        <label class="lm-label">Start Date <span class="lm-req">*</span></label>
                        <input type="date" class="lm-input" v-model="project.project_start_date" />
                      </div>
                      <div class="lm-field">
                        <label class="lm-label">Status <span class="lm-req">*</span></label>
                        <select class="lm-input" v-model="project.status">
                          <option value="" disabled selected>Select status</option>
                          <option value="1">Completed</option>
                          <option value="2">On-going</option>
                        </select>
                      </div>
                      <div v-if="project.status == 1" class="lm-field">
                        <label class="lm-label">End Date <span class="lm-req">*</span></label>
                        <input type="date" class="lm-input" v-model="project.project_end_date" />
                      </div>
                    </div>

                    <div class="lm-field">
                      <label class="lm-label">Location <span class="lm-req">*</span></label>
                      <div class="lm-row lm-row--3">
                        <select class="lm-input" v-model="project.country_id" @change="fetchStates($event)">
                          <option value="" disabled selected>Country</option>
                          <option v-for="data in country" :key="data.id" :value="data.id">{{data.name}}</option>
                        </select>
                        <select class="lm-input" v-model="project.state_id" @change="fetchCities($event)">
                          <option value="" disabled selected>State</option>
                          <option v-for="data in state" :key="data.id" :value="data.id">{{data.name}}</option>
                        </select>
                        <select class="lm-input" v-model="project.city_id">
                          <option value="" disabled selected>City</option>
                          <option v-for="data in city" :key="data.id" :value="data.id">{{data.name}}</option>
                        </select>
                      </div>
                    </div>

                    <div class="lm-field">
                      <label class="lm-label">Address</label>
                      <textarea class="lm-input" v-model="project.address" rows="2" style="resize:vertical"></textarea>
                    </div>

                    <div class="lm-field">
                      <label class="lm-label">Height</label>
                      <select class="lm-input" v-model="project.height">
                        <option value="" disabled selected>Select height</option>
                        <option value="Low rise (Less than 3 Storeys)">Low rise (Less than 3 Storeys)</option>
                        <option value="Mid rise (4 - 10 Storeys)">Mid rise (4 - 10 Storeys)</option>
                        <option value="High rise (11 - 30 Storeys)">High rise (11 - 30 Storeys)</option>
                        <option value="Skyscrappers (above 30 storeys)">Skyscrappers (above 30 storeys)</option>
                      </select>
                    </div>

                    <div class="lm-field">
                      <label class="lm-label">Client / Developer <span class="lm-req">*</span></label>
                      <multiselect v-model="project.client" label="name" placeholder="Select one" :options="users" :allow-empty="true"></multiselect>
                    </div>

                    <div class="lm-field">
                      <div class="lm-label-row">
                        <label class="lm-label">Architect <span class="lm-req">*</span></label>
                        <button type="button" class="lm-add-link" @click="addArchitectTeam">+ Add Team Member</button>
                      </div>
                      <multiselect v-model="project.architect" label="name" placeholder="Select one" :options="users" :allow-empty="true"></multiselect>
                      <div v-for="(input, index) in project.architect_team" :key="index" class="lm-team-row">
                        <model-list-select :list="users" v-model="input.user_id" option-value="id" option-text="name" placeholder="search team"></model-list-select>
                        <button type="button" @click="deleteArchitectTeam(index)" class="lm-team-del">×</button>
                      </div>
                    </div>

                    <div class="lm-field">
                      <div class="lm-label-row">
                        <label class="lm-label">Structural Engineer <span class="lm-req">*</span></label>
                        <button type="button" class="lm-add-link" @click="addStructuralEngineerTeam">+ Add Team Member</button>
                      </div>
                      <multiselect v-model="project.structural_engineer" label="name" placeholder="Select one" :options="users" :allow-empty="true"></multiselect>
                      <div v-for="(input, index) in project.structural_engineer_team" :key="index" class="lm-team-row">
                        <model-list-select :list="users" v-model="input.user_id" option-value="id" option-text="name" placeholder="search team"></model-list-select>
                        <button type="button" @click="deleteStructuralEngineerTeam(index)" class="lm-team-del">×</button>
                      </div>
                    </div>

                    <div class="lm-field">
                      <div class="lm-label-row">
                        <label class="lm-label">MEP Engineer <span class="lm-req">*</span></label>
                        <button type="button" class="lm-add-link" @click="addMepEngineerTeam">+ Add Team Member</button>
                      </div>
                      <multiselect v-model="project.mep_engineer" label="name" placeholder="Select one" :options="users" :allow-empty="true"></multiselect>
                      <div v-for="(input, index) in project.mep_engineer_team" :key="index" class="lm-team-row">
                        <model-list-select :list="users" v-model="input.user_id" option-value="id" option-text="name" placeholder="search team"></model-list-select>
                        <button type="button" @click="deleteMepEngineerTeam(index)" class="lm-team-del">×</button>
                      </div>
                    </div>

                    <div class="lm-field">
                      <div class="lm-label-row">
                        <label class="lm-label">Quantity Surveyor <span class="lm-req">*</span></label>
                        <button type="button" class="lm-add-link" @click="addSurveyorTeam">+ Add Team Member</button>
                      </div>
                      <multiselect v-model="project.quantity_surveyor" label="name" placeholder="Select one" :options="users" :allow-empty="true"></multiselect>
                      <div v-for="(input, index) in project.quantity_surveyor_team" :key="index" class="lm-team-row">
                        <model-list-select :list="users" v-model="input.user_id" option-value="id" option-text="name" placeholder="search team"></model-list-select>
                        <button type="button" @click="deleteSurveyorTeam(index)" class="lm-team-del">×</button>
                      </div>
                    </div>

                    <div class="lm-field">
                      <div class="lm-label-row">
                        <label class="lm-label">Main Contractor <span class="lm-req">*</span></label>
                        <button type="button" class="lm-add-link" @click="addMainContractorTeam">+ Add Team Member</button>
                      </div>
                      <multiselect v-model="project.main_contractor" label="name" placeholder="Select one" :options="users" :allow-empty="true"></multiselect>
                      <div v-for="(input, index) in project.main_contractor_team" :key="index" class="lm-team-row">
                        <model-list-select :list="users" v-model="input.user_id" option-value="id" option-text="name" placeholder="search team"></model-list-select>
                        <button type="button" @click="deleteMainContractorTeam(index)" class="lm-team-del">×</button>
                      </div>
                    </div>

                    <div class="lm-field">
                      <div class="lm-label-row">
                        <label class="lm-label">Mechanical Contractor <span class="lm-req">*</span></label>
                        <button type="button" class="lm-add-link" @click="addMechanicalContractorTeam">+ Add Team Member</button>
                      </div>
                      <multiselect v-model="project.mechanical_contractor" label="name" placeholder="Select one" :options="users" :allow-empty="true"></multiselect>
                      <div v-for="(input, index) in project.mechanical_contractor_team" :key="index" class="lm-team-row">
                        <model-list-select :list="users" v-model="input.user_id" option-value="id" option-text="name" placeholder="search team"></model-list-select>
                        <button type="button" @click="deleteMechanicalContractorTeam(index)" class="lm-team-del">×</button>
                      </div>
                    </div>

                    <div class="lm-field">
                      <div class="lm-label-row">
                        <label class="lm-label">Electrical Contractor <span class="lm-req">*</span></label>
                        <button type="button" class="lm-add-link" @click="addElectricalContractorTeam">+ Add Team Member</button>
                      </div>
                      <multiselect v-model="project.electrical_contractor" label="name" placeholder="Select one" :options="users" :allow-empty="true"></multiselect>
                      <div v-for="(input, index) in project.electrical_contractor_team" :key="index" class="lm-team-row">
                        <model-list-select :list="users" v-model="input.user_id" option-value="id" option-text="name" placeholder="search team"></model-list-select>
                        <button type="button" @click="deleteElectricalContractorTeam(index)" class="lm-team-del">×</button>
                      </div>
                    </div>

                    <div class="lm-field">
                      <div class="lm-label-row">
                        <label class="lm-label">Piling Contractor</label>
                        <button type="button" class="lm-add-link" @click="addPilingTeam">+ Add Team Member</button>
                      </div>
                      <multiselect v-model="project.piling_contractor" label="name" placeholder="Select one" :options="users" :allow-empty="true"></multiselect>
                      <div v-for="(input, index) in project.piling_contractor_team" :key="index" class="lm-team-row">
                        <model-list-select :list="users" v-model="input.user_id" option-value="id" option-text="name" placeholder="search team"></model-list-select>
                        <button type="button" @click="deletePilingTeam(index)" class="lm-team-del">×</button>
                      </div>
                    </div>

                    <div class="lm-field">
                      <div class="lm-label-row">
                        <label class="lm-label">Lift Contractor</label>
                        <button type="button" class="lm-add-link" @click="addLiftTeam">+ Add Team Member</button>
                      </div>
                      <multiselect v-model="project.lift_contractor" label="name" placeholder="Select one" :options="users" :allow-empty="true"></multiselect>
                      <div v-for="(input, index) in project.lift_contractor_team" :key="index" class="lm-team-row">
                        <model-list-select :list="users" v-model="input.user_id" option-value="id" option-text="name" placeholder="search team"></model-list-select>
                        <button type="button" @click="deleteLiftTeam(index)" class="lm-team-del">×</button>
                      </div>
                    </div>

                    <div class="lm-field">
                      <div class="lm-label-row">
                        <label class="lm-label">Project Manager</label>
                        <button type="button" class="lm-add-link" @click="addPMTeam">+ Add Team Member</button>
                      </div>
                      <multiselect v-model="project.project_manager" label="name" placeholder="Select one" :options="users" :allow-empty="true"></multiselect>
                      <div v-for="(input, index) in project.project_manager_team" :key="index" class="lm-team-row">
                        <model-list-select :list="users" v-model="input.user_id" option-value="id" option-text="name" placeholder="search team"></model-list-select>
                        <button type="button" @click="deletePMTeam(index)" class="lm-team-del">×</button>
                      </div>
                    </div>

                    <div class="lm-field">
                      <div class="lm-label-row">
                        <label class="lm-label">Vendors</label>
                        <button type="button" class="lm-add-link" @click="addVendor">+ Add Vendor</button>
                      </div>
                      <div v-for="(input, index) in project.project_vendors" :key="index" class="lm-vendor-row">
                        <model-list-select :list="vendor_category" v-model="input.vendor" option-value="id" option-text="name" placeholder="select category"></model-list-select>
                        <model-list-select :list="users" v-model="input.user_id" option-value="id" option-text="name" placeholder="search user"></model-list-select>
                        <button type="button" @click="deleteVendor(index)" class="lm-team-del">×</button>
                      </div>
                    </div>

                    <div class="lm-field">
                      <label class="lm-label">YouTube URL</label>
                      <input type="url" class="lm-input" v-model="project.youtube_url" placeholder="https://www.youtube.com/watch?v=..." />
                    </div>

                    <div class="lm-field">
                      <label class="lm-label">Instagram URL</label>
                      <input type="url" class="lm-input" v-model="project.instagram_url" placeholder="https://www.instagram.com/p/..." />
                    </div>

                    <div class="lm-field">
                      <label class="lm-label">Project Images <span class="lm-req">*</span></label>
                      <vue-upload-multiple-image
                        @upload-success="uploadImageSuccess"
                        @before-remove="beforeRemove"
                        @edit-image="editImage"
                        @data-change="dataChange"
                        :data-images="images"
                        dragText="Drag and drop images"
                        browseText="Browse Images"
                        primaryText="added"
                        markIsPrimaryText="Set default image"
                        popupText="Description default image"
                        :maxImage="5"
                      ></vue-upload-multiple-image>
                    </div>

                  </div>
                </template>

                <!-- ── Property Form ── -->
                <template v-if="type === 'property'">
                  <div class="lm-section-header">
                    <h6 class="lm-section-title">Property Details</h6>
                  </div>
                  <div class="lm-form">

                    <div class="lm-field">
                      <label class="lm-label">Property Name <span class="lm-req">*</span></label>
                      <input type="text" class="lm-input" v-model="property.name" placeholder="Enter property name" />
                    </div>

                    <div class="lm-field">
                      <label class="lm-label">Category <span class="lm-req">*</span></label>
                      <div class="lm-category-row">
                        <select class="lm-input" v-model="property.project_interest_1" @change="fectchInterestChildren($event)">
                          <option value="" disabled selected>Main category</option>
                          <option v-for="data in property_interests" :key="data.id" :value="data.id">{{data.name}}</option>
                        </select>
                        <select v-if="interest_children.length" class="lm-input" v-model="property.project_interest_2" @change="fectchInterestChildren2($event)">
                          <option value="" disabled selected>Sub-category</option>
                          <option v-for="data in interest_children" :key="data.id" :value="data.id">{{data.name}}</option>
                        </select>
                        <select v-if="interest_children2.length" class="lm-input" v-model="property.project_interest_3">
                          <option value="" disabled selected>Sub-category 2</option>
                          <option v-for="data in interest_children2" :key="data.id" :value="data.id">{{data.name}}</option>
                        </select>
                      </div>
                    </div>

                    <div class="lm-field">
                      <label class="lm-label">Description <span class="lm-req">*</span></label>
                      <div class="lm-quill">
                        <QuillEditor id="property_editor" v-model:content="property.description" contentType="html" :toolbar="customToolbar" />
                      </div>
                    </div>

                    <div class="lm-field">
                      <label class="lm-label">Property Type <span class="lm-req">*</span></label>
                      <select class="lm-input" v-model="property.type">
                        <option value="" disabled selected>Select type</option>
                        <option value="Residential">Residential</option>
                        <option value="Commercial">Commercial</option>
                        <option value="Residential | Commercial">Residential | Commercial</option>
                      </select>
                    </div>

                    <div class="lm-field">
                      <label class="lm-label">Location <span class="lm-req">*</span></label>
                      <div class="lm-row lm-row--3">
                        <select class="lm-input" v-model="property.country_id" @change="fetchStates($event)">
                          <option value="" disabled selected>Country</option>
                          <option v-for="data in country" :key="data.id" :value="data.id">{{data.name}}</option>
                        </select>
                        <select class="lm-input" v-model="property.state_id" @change="fetchCities($event)">
                          <option value="" disabled selected>State</option>
                          <option v-for="data in state" :key="data.id" :value="data.id">{{data.name}}</option>
                        </select>
                        <select class="lm-input" v-model="property.city_id">
                          <option value="" disabled selected>City</option>
                          <option v-for="data in city" :key="data.id" :value="data.id">{{data.name}}</option>
                        </select>
                      </div>
                    </div>

                    <div class="lm-field">
                      <label class="lm-label">Address</label>
                      <input type="text" class="lm-input" v-model="property.address" />
                    </div>

                    <div class="lm-field">
                      <label class="lm-label">Currency</label>
                      <select class="lm-input" v-model="property.currency_id">
                        <option v-for="data in currency" :key="data.id" :value="data.id">{{data.symbol}} - {{data.name}}</option>
                      </select>
                    </div>

                    <div class="lm-field">
                      <label class="lm-label">Pricing</label>
                      <div class="lm-radio-group">
                        <label class="lm-radio"><input type="radio" v-model="price_type" value="single" /> Single Price</label>
                        <label class="lm-radio"><input type="radio" v-model="price_type" value="multiple" /> Different Prices</label>
                      </div>
                    </div>

                    <div v-if="price_type === 'single'" class="lm-field">
                      <label class="lm-label">Property Price <span class="lm-req">*</span></label>
                      <input type="number" class="lm-input" v-model="property.price" />
                    </div>

                    <div v-if="price_type === 'multiple'" class="lm-field">
                      <div class="lm-label-row">
                        <label class="lm-label">Property Prices <span class="lm-req">*</span></label>
                        <button type="button" class="lm-add-link" @click="addPrice">+ Add Price</button>
                      </div>
                      <div v-for="(input, index) in property.prices" :key="index" class="lm-price-row">
                        <input type="text" class="lm-input" v-model="input.price" placeholder="Amount" onkeypress="if ( isNaN(this.value + String.fromCharCode(event.keyCode) )) return false;" />
                        <input type="text" class="lm-input" v-model="input.title" placeholder="Title" />
                        <button type="button" @click="deletePrice(index)" class="lm-team-del">×</button>
                      </div>
                    </div>

                    <div class="lm-field">
                      <label class="lm-radio">
                        <input type="checkbox" v-model="more_detail" value="1" />
                        <span style="margin-left:6px">Add More Property Details</span>
                      </label>
                    </div>

                    <div v-if="more_detail == '1'" class="lm-field">
                      <div class="lm-label-row">
                        <label class="lm-label">Property Details</label>
                        <button type="button" class="lm-add-link" @click="addDetail">+ Add Detail</button>
                      </div>
                      <div v-for="(input, index) in property.details" :key="index" class="lm-price-row">
                        <input type="text" class="lm-input" v-model="input.title" placeholder="Title" />
                        <input type="text" class="lm-input" v-model="input.description" placeholder="Description" />
                        <button type="button" @click="deleteDetail(index)" class="lm-team-del">×</button>
                      </div>
                    </div>

                    <div class="lm-field">
                      <label class="lm-label">YouTube URL</label>
                      <input type="url" class="lm-input" v-model="property.youtube_url" placeholder="https://www.youtube.com/watch?v=..." />
                    </div>

                    <div class="lm-field">
                      <label class="lm-label">Instagram URL</label>
                      <input type="url" class="lm-input" v-model="property.instagram_url" placeholder="https://www.instagram.com/p/..." />
                    </div>

                    <div class="lm-field">
                      <label class="lm-label">Property Images <span class="lm-req">*</span></label>
                      <vue-upload-multiple-image
                        @upload-success="uploadImageProperty"
                        @before-remove="beforeRemove"
                        @edit-image="editImage"
                        @data-change="dataChange"
                        :data-images="images"
                        dragText="Drag and drop images"
                        browseText="Browse Images"
                        primaryText="added"
                        markIsPrimaryText="Set default image"
                        popupText="Description default image"
                        :maxImage="5"
                      ></vue-upload-multiple-image>
                    </div>

                  </div>
                </template>








                <!-- ── Plan (Concept) Form ── -->
                <template v-if="type === 'plan'">
                  <div class="lm-section-header">
                    <h6 class="lm-section-title">Concept Details</h6>
                  </div>
                  <div class="lm-form">

                    <div class="lm-field">
                      <label class="lm-label">Title <span class="lm-req">*</span></label>
                      <input type="text" class="lm-input" v-model="plan.title" placeholder="Enter concept title" />
                    </div>

                    <div class="lm-field">
                      <label class="lm-label">Category <span class="lm-req">*</span></label>
                      <div class="lm-category-row">
                        <select class="lm-input" v-model="plan.project_interest_1" @change="fectchInterestChildren($event)">
                          <option value="" disabled selected>Main category</option>
                          <option v-for="data in plan_interests" :key="data.id" :value="data.id">{{data.name}}</option>
                        </select>
                        <select v-if="interest_children.length" class="lm-input" v-model="plan.project_interest_2" @change="fectchInterestChildren2($event)">
                          <option value="" disabled selected>Sub-category</option>
                          <option v-for="data in interest_children" :key="data.id" :value="data.id">{{data.name}}</option>
                        </select>
                        <select v-if="interest_children2.length" class="lm-input" v-model="plan.project_interest_3">
                          <option value="" disabled selected>Sub-category 2</option>
                          <option v-for="data in interest_children2" :key="data.id" :value="data.id">{{data.name}}</option>
                        </select>
                      </div>
                    </div>

                    <div class="lm-field">
                      <label class="lm-label">Description <span class="lm-req">*</span></label>
                      <div class="lm-quill">
                        <QuillEditor id="plan_editor" v-model:content="plan.description" contentType="html" :toolbar="customToolbar" />
                      </div>
                    </div>

                    <div class="lm-field">
                      <label class="lm-label">Height</label>
                      <select class="lm-input" v-model="plan.height">
                        <option value="" disabled selected>Select height</option>
                        <option value="Low rise (Less than 3 Storeys)">Low rise (Less than 3 Storeys)</option>
                        <option value="Mid rise (4 - 10 Storeys)">Mid rise (4 - 10 Storeys)</option>
                        <option value="High rise (11 - 30 Storeys)">High rise (11 - 30 Storeys)</option>
                        <option value="Skyscrappers (above 30 storeys)">Skyscrappers (above 30 storeys)</option>
                      </select>
                    </div>

                    <div class="lm-field">
                      <label class="lm-label">Land Size <span class="lm-req">*</span></label>
                      <div class="lm-row">
                        <input type="text" class="lm-input" v-model="plan.land_size" placeholder="Size" oninput="this.value = this.value.replace(/[^0-9.]/g, '').replace(/(\..?)\../g, '$1');" />
                        <select class="lm-input" v-model="plan.land_size_unit">
                          <option value="" disabled selected>Unit</option>
                          <option value="M">M²</option>
                          <option value="Plot">Plot</option>
                          <option value="Acre">Acre</option>
                          <option value="Hectares">Hectares</option>
                          <option value="KM">KM²</option>
                        </select>
                      </div>
                    </div>

                    <div class="lm-field">
                      <label class="lm-label">YouTube URL</label>
                      <input type="url" class="lm-input" v-model="plan.youtube_url" placeholder="https://www.youtube.com/watch?v=..." />
                    </div>

                    <div class="lm-field">
                      <label class="lm-label">Instagram URL</label>
                      <input type="url" class="lm-input" v-model="plan.instagram_url" placeholder="https://www.instagram.com/p/..." />
                    </div>

                    <div class="lm-field">
                      <label class="lm-label">Concept Images <span class="lm-req">*</span></label>
                      <vue-upload-multiple-image
                        @upload-success="uploadImagePlan"
                        @before-remove="beforeRemove"
                        @edit-image="editImage"
                        @data-change="dataChange"
                        :data-images="images"
                        dragText="Drag and drop images"
                        browseText="Browse Images"
                        primaryText="added"
                        markIsPrimaryText="Set default image"
                        popupText="Description default image"
                        :maxImage="5"
                      ></vue-upload-multiple-image>
                    </div>

                  </div>
                </template>

              </template>

            </div>

            <div class="lm-footer">
              <template v-if="created">
                <button type="button" class="lm-btn lm-btn--primary" @click="closeForm()" data-dismiss="modal">Close</button>
              </template>
              <template v-else>
                <button type="button" class="lm-btn lm-btn--outline" @click="cancelForm()">Cancel</button>
                <button type="button" class="lm-btn lm-btn--primary" @click="createProject()" :disabled="loading || !type">
                  <span v-if="loading" class="lm-spinner"></span>
                  <span v-else>Submit Listing</span>
                </button>
              </template>
            </div>

          </div>
        </div>
      </div>





      <!-- likes Modal -->
      <div class="modal fade" id="project-likes" tabindex="-1" role="dialog" aria-labelledby="exampleModalLabel" aria-hidden="true">
        <div class="modal-dialog" role="document">
          <div class="modal-content">
            <div class="modal-header">
              <!-- <h5 class="modal-title"></h5> -->
              <button type="button" class="close" data-dismiss="modal" aria-label="Close">
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div v-if="currentData.project">
              <div v-if="currentData.project.likes" class="modal-body">
                <div v-for="data in currentData.project.likes" :key="data.id" class="p-3 d-flex align-items-center border-bottom osahan-post-header">
                  <div class="dropdown-list-image mr-3">
                    <a :href="'/sc/profile/'+data.user.slug">
                    <img v-if="data.user.avatar" class="rounded-circle" :src="image_url+'users/'+data.user.avatar" style="max-height: 150px; max-width: 150px" alt="">
                    <img v-else class="rounded-circle" src="/assets/avatar.png" style="max-height: 150px; max-width: 150px" alt="">
                    </a>
                  </div>
                  <div class="font-weight-bold">
                    <div class="text-truncate"><router-link :to="'/sc/profile/'+data.user.slug">{{data.user.name}}</router-link></div>
                    <div class="small text-gray-500">{{data.user.profession}} </div>
                  </div>
                  <span class="ml-auto"><br><button type="button" @click="follow(data.user.id)" class="btn btn-outline-primary btn-sm">Follow</button></span> 
                </div>
              </div>
            </div>
            <!-- <div class="modal-footer">
              <div>
                <button type="button" @click="closeModal()" class="btn btn-secondary" data-dismiss="modal">Close</button>&nbsp;
              </div>
            </div> -->

          </div>
        </div>
      </div>




      <!-- property likes view Modal -->
      <div class="modal fade" id="post-likes" tabindex="-1" role="dialog" aria-labelledby="exampleModalLabel" aria-hidden="true">
        <div class="modal-dialog" role="document">
          <div class="modal-content">
            <div class="modal-header">
              <!-- <h5 class="modal-title"></h5> -->
              <button type="button" class="close" data-dismiss="modal" aria-label="Close">
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div v-if="currentData">
              <div v-if="currentData.likes" class="modal-body">
                <div v-for="data in currentData.likes" :key="data.id" class="p-3 d-flex align-items-center border-bottom osahan-post-header">
                  <div class="dropdown-list-image mr-3">
                    <a :href="'/sc/profile/'+data.user.slug">
                    <img v-if="data.user.avatar" class="rounded-circle" :src="image_url+'users/'+data.user.avatar" style="max-height: 150px; max-width: 150px" alt="">
                    <img v-else class="rounded-circle" src="/assets/avatar.png" style="max-height: 150px; max-width: 150px" alt="">
                    </a>
                  </div>
                  <div class="font-weight-bold">
                    <div class="text-truncate"><router-link :to="'/sc/profile/'+data.user.slug">{{data.user.name}}</router-link></div>
                    <div class="small text-gray-500">{{data.user.profession}} </div>
                  </div>
                  <span class="ml-auto"><br><button type="button" @click="follow(data.user.id)" class="btn btn-outline-primary btn-sm">Follow</button></span> 
                </div>
              </div>
            </div>
            <!-- <div class="modal-footer">
              <div>
                <button type="button" @click="closeModal()" class="btn btn-secondary" data-dismiss="modal">Close</button>&nbsp;
              </div>
            </div> -->

          </div>
        </div>
      </div>




      <!-- plan likes view Modal -->
      <div class="modal fade" id="plan-likes" tabindex="-1" role="dialog" aria-labelledby="exampleModalLabel" aria-hidden="true">
        <div class="modal-dialog" role="document">
          <div class="modal-content">
            <div class="modal-header">
              <!-- <h5 class="modal-title"></h5> -->
              <button type="button" class="close" data-dismiss="modal" aria-label="Close">
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div v-if="currentData">
              <div v-if="currentData.likes" class="modal-body">
                <div v-for="data in currentData.likes" :key="data.id" class="p-3 d-flex align-items-center border-bottom osahan-post-header">
                  <div class="dropdown-list-image mr-3">
                    <a :href="'/sc/profile/'+data.user.slug">
                    <img v-if="data.user.avatar" class="rounded-circle" :src="image_url+'users/'+data.user.avatar" style="max-height: 150px; max-width: 150px" alt="">
                    <img v-else class="rounded-circle" src="/assets/avatar.png" style="max-height: 150px; max-width: 150px" alt="">
                    </a>
                  </div>
                  <div class="font-weight-bold">
                    <div class="text-truncate"><router-link :to="'/sc/profile/'+data.user.slug">{{data.user.name}}</router-link></div>
                    <div class="small text-gray-500">{{data.user.profession}} </div>
                  </div>
                  <span class="ml-auto"><br><button type="button" @click="follow(data.user.id)" class="btn btn-outline-primary btn-sm">Follow</button></span> 
                </div>
              </div>
            </div>
            <!-- <div class="modal-footer">
              <div>
                <button type="button" @click="closeModal()" class="btn btn-secondary" data-dismiss="modal">Close</button>&nbsp;
              </div>
            </div> -->

          </div>
        </div>
      </div>





      <!-- comments Modal -->
      <div class="modal fade" id="project-comments" tabindex="-1" role="dialog" aria-labelledby="exampleModalLabel" aria-hidden="true">
        <div class="modal-dialog" role="document">
          <div class="modal-content">
            <div class="modal-header">
              <button type="button" class="close" data-dismiss="modal" aria-label="Close">
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div v-if="currentData.project">
              <div v-if="currentData.project.comments" class="modal-body">
                <div v-for="com in currentData.project.comments" :key="com.id" class="p-3 d-flex align-items-top border-bottom osahan-post-comment">
                  <div class="dropdown-list-image mr-3">
                    <img v-if="com.user.avatar" class="rounded-circle" :src="image_url+'users/'+com.user.avatar" style="max-height: 150px; max-width: 150px" alt="">
                    <img v-else class="rounded-circle" src="/assets/avatar.png" style="max-height: 150px; max-width: 150px" alt="">
                    <div class="status-indicator bg-success"></div>
                  </div>
                  <div class="font-weight-bold">
                  <div class="text-truncate"> {{com.user.name}} <span class="float-right small">{{formatDate(com.created_at)}}</span></div>
                  <div class="small text-gray-500">{{com.comment}}</div>
                  </div>
                </div>
              </div>
            </div>
            <!-- <div class="modal-footer">
              <div>
                <button type="button" @click="closeModal()" class="btn btn-secondary" data-dismiss="modal">Close</button>&nbsp;
              </div>
            </div> -->

          </div>
        </div>
      </div>



      <!-- property comments view Modal -->
      <div class="modal fade" id="post-comments" tabindex="-1" role="dialog" aria-labelledby="exampleModalLabel" aria-hidden="true">
        <div class="modal-dialog" role="document">
          <div class="modal-content">
            <div class="modal-header">
              <button type="button" class="close" data-dismiss="modal" aria-label="Close">
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div v-if="currentData">
              <div v-if="currentData.comments" class="modal-body">
                <div v-for="com in currentData.comments" :key="com.id" class="p-3 d-flex align-items-top border-bottom osahan-post-comment">
                  <div class="dropdown-list-image mr-3">
                    <img v-if="com.user.avatar" class="rounded-circle" :src="image_url+'users/'+com.user.avatar" style="max-height: 150px; max-width: 150px" alt="">
                    <img v-else class="rounded-circle" src="/assets/avatar.png" style="max-height: 150px; max-width: 150px" alt="">
                    <div class="status-indicator bg-success"></div>
                  </div>
                  <div class="font-weight-bold">
                  <div class="text-truncate"> {{com.user.name}} <span class="float-right small">{{formatDate(com.created_at)}}</span></div>
                  <div class="small text-gray-500">{{com.comment}}</div>
                  </div>
                </div>
              </div>
            </div>
            <!-- <div class="modal-footer">
              <div>
                <button type="button" @click="closeModal()" class="btn btn-secondary" data-dismiss="modal">Close</button>&nbsp;
              </div>
            </div> -->

          </div>
        </div>
      </div>





      <!-- plan comments view Modal -->
      <div class="modal fade" id="plan-comments" tabindex="-1" role="dialog" aria-labelledby="exampleModalLabel" aria-hidden="true">
        <div class="modal-dialog" role="document">
          <div class="modal-content">
            <div class="modal-header">
              <button type="button" class="close" data-dismiss="modal" aria-label="Close">
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div v-if="currentData">
              <div v-if="currentData.comments" class="modal-body">
                <div v-for="com in currentData.comments" :key="com.id" class="p-3 d-flex align-items-top border-bottom osahan-post-comment">
                  <div class="dropdown-list-image mr-3">
                    <img v-if="com.user.avatar" class="rounded-circle" :src="image_url+'users/'+com.user.avatar" style="max-height: 150px; max-width: 150px" alt="">
                    <img v-else class="rounded-circle" src="/assets/avatar.png" style="max-height: 150px; max-width: 150px" alt="">
                    <div class="status-indicator bg-success"></div>
                  </div>
                  <div class="font-weight-bold">
                  <div class="text-truncate"> {{com.user.name}} <span class="float-right small">{{formatDate(com.created_at)}}</span></div>
                  <div class="small text-gray-500">{{com.comment}}</div>
                  </div>
                </div>
              </div>
            </div>
            <!-- <div class="modal-footer">
              <div>
                <button type="button" @click="closeModal()" class="btn btn-secondary" data-dismiss="modal">Close</button>&nbsp;
              </div>
            </div> -->

          </div>
        </div>
      </div>




      <!-- update banner Modal -->
      <div class="modal fade" id="update-banner" tabindex="-1" role="dialog" aria-labelledby="exampleModalLabel" aria-hidden="true">
        <div class="modal-dialog" role="document">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title" id="exampleModalLabel">Update Company Banner </h5>
              <button type="button" class="close" data-dismiss="modal" aria-label="Close">
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div class="modal-body">
              <div id="banner-form">
                <div class="row">                  
                  <div class="col-md-8 mb-8 mb-sm-12">
                    <div class="js-form-message">
                      <label id="nameLabel" class="form-label">Select Image
                        <span class="text-danger" style="font-size: 10px">* (1500px X 300px)</span>
                      </label>
                      <div class="form-group">
                        <input class="form-control" type="file" name="banner" accept="image/*"  @change="setImage" required/>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-4 mb-4 mb-sm-12">
                    <div class="js-form-message">
                     <div class="form-group">
                        <img :src="imageSrc" style="width: 100px;" />
                      </div>
                    </div>
                  </div>
                </div>

                <div v-if="this.imageSrc" class="row">                  
                  <div class="col-md-12 mb-12 mb-sm-12">
                    <div class="my-3 d-flex align-items-center justify-content-center mx-auto">
                    <vue-cropper
                      class="mr-2 w-50"
                      ref="cropper"
                      :guides="true"
                      :aspect-ratio="1500 / 400"
                      :src="imageSrc"
                    ></vue-cropper>
                    </div>
                  </div>
                  <div class="col-md-12 mb-12 mb-sm-12">
                    <!-- Cropped image previewer -->
                    <img class="ml-2 w-50 bg-light my-3 d-flex align-items-center justify-content-center mx-auto" :src="croppedImageSrc" />
                  </div>
                </div>
              </div>
            </div>
            <div class="modal-footer">
               <div v-if="loading">
                <button type="button" class="btn btn-primary" data-dismiss="modal">Loading.....</button>
              </div>
              <div v-else>
                <button v-if="this.imageSrc" @click="cropImage" type="button" class="btn btn-primary">Crop</button>&nbsp; &nbsp; 
                <button v-if="this.croppedImageSrc" @click="uploadImage(company.id)" type="button" class="btn btn-primary">Upload Image</button>
              </div>
            </div>

          </div>
        </div>
      </div>






    </div><!-- .co-page -->
  </public-layout>
</template>
<script>
import PublicLayout from "@/layouts/PublicLayout";
import axios from 'axios';
import moment from 'moment';
import $ from "jquery";
import { useSEO } from '@/utils/seo.js';
import ModelListSelect from '@/components/ModelListSelect';

import Multiselect from 'vue-multiselect';
import VueUploadMultipleImage from '@/components/VueUploadMultipleImage';
import VueCropper from 'vue-cropperjs';
import 'cropperjs/dist/cropper.css';
import { QuillEditor } from '@vueup/vue-quill';
import '@vueup/vue-quill/dist/vue-quill.snow.css';

export default {
  name: 'CompanyDetail',

  components: {
    PublicLayout,
    VueUploadMultipleImage,
    ModelListSelect,
    Multiselect,
    VueCropper,
    QuillEditor,
  },

    created() {
        this.fetchProjects();
        this.fetchPlans();
        this.fetchCompany();
        this.fetchUserCompanies();
        this.fetchCountries();
        this.fetchNigeriaStates();
        this.fetchLagosCities();
        this.fetchUsers();
        this.fetchVendorCategory();
        this.fectchInterests();
        this.fetchCompanySetting();
        this.fetchIndustries();
        this.fetchCurrency();
        this.fectchPropertyInterests();
        this.fectchPlanInterests();
        this.fetchProperties();
    },

    watch: {
      $route: {
          immediate: true,
          handler(to) {
            let subject = to.params.id.substring(0, to.params.id.length - 10);
            document.title = to.meta.title || subject.replace(/-/g, " ")+' - Structurecity';
          }
      },
  }, 

    data() {
        return {
          cdVisible: false,
          cdType: '',
          cdId: null,
          cdName: '',
          cdLoading: false,
          activeTab: 'projects',
          readMore: {},
          price_type: 'single',
          more_detail: '',
          type: '',
          loading:false,
          created:false,
          myCompanies: [],
          postingAs: null,
          editLogoFile: null,
          editLogoPreview: '',
          settings: {},
          projects: [],
          plans: [],
          properties: [],
          industries: [],
          currency:[],
          vendor_category:[],
          interests: [],
          property_interests: [],
          plan_interests: [],
          users: [],
          images: [],
          interest_children: [],
          interest_children2: [],
          connections: [],
          currentData: {},
          company:{},
          check_follower: 0,
          total_follower: 0,
          followers: [],
          id: localStorage.getItem('user'),
          country: [],
          state: [],
          city: [],
          project: {
            user_id: '',
            name: '',
            project_interest_1: '',
            project_interest_2: '',
            project_interest_3: '',
            description: '',
            project_start_date: '',
            project_end_date: '',
            country_id: 160,
            state_id: '',
            city_id: '',
            status: '',
            height: '',
            address: '',
            architect: '',
            structural_engineer: '',
            mep_engineer: '',
            client: '',
            quantity_surveyor: '',
            main_contractor: '',
            mechanical_contractor: '',
            electrical_contractor: '',
            piling_contractor: '',
            lift_contractor: '',
            project_manager: '',
            architect_team: [],
            structural_engineer_team: [],
            mep_engineer_team: [],
            quantity_surveyor_team: [],
            main_contractor_team: [],
            mechanical_contractor_team: [],
            electrical_contractor_team: [],
            piling_contractor_team: [],
            lift_contractor_team:[],
            project_manager_team:[],
            project_vendors: [],
            youtube_url: '',
            instagram_url: '',
            images:'',
          },
          comment: {
            description: [],
          },

          property: {
            user_id: '',
            name: '',
            project_interest_1: '',
            project_interest_2: '',
            project_interest_3: '',
            description: '',
            currency_id: '',
            country_id: 160,
            state_id: '',
            city_id: '',
            address: '',
            price: '',
            type: '',
            prices: [],
            details: [],
            youtube_url: '',
            instagram_url: '',
            images:'',
          },

          plan: {
            user_id: localStorage.getItem('user'),
            title: '',
            project_interest_1: '',
            project_interest_2: '',
            project_interest_3: '',
            description: '',
            height: '',
            land_size: '',
            land_size_unit: '',
            youtube_url: '',
            instagram_url: '',
            images:'',
          },

          imageSrc: "",
          croppedImageSrc: "",

          customToolbar: [
            ["bold", "italic", "underline"],
            [{ list: "ordered" }, { list: "bullet" }],
            // ["image", "code-block"]
          ],
        }
    },

    methods: {
        fetchProjects () {
            axios(process.env.VUE_APP_API_URL + 'user/company-created-projects/'+this.$route.params.id).then(response =>{
                if (response.status !== 200) return;
                this.projects = response.data;
            }).catch(() => {});
        },

        fetchProperties () {
            axios(process.env.VUE_APP_API_URL + 'user/company-properties/'+this.$route.params.id).then(response =>{
                if (response.status !== 200) return;
                this.properties = response.data;
            }).catch(() => {});
        },

        fetchPlans () {
            axios(process.env.VUE_APP_API_URL + 'user/company-plans/'+this.$route.params.id).then(response =>{
                if (response.status !== 200) return;
                this.plans = response.data;
            }).catch(() => {});
        },

        fetchCompany () {
            axios(process.env.VUE_APP_API_URL + 'user/company/details/'+this.$route.params.id).then(response =>{
                if (! response.status == 200) {
                    return this.errorMessage = 'Could not fetch data';
                }
                this.company = response.data;
                if (!this.company.industry_id && this.company.industry) {
                    this.company.industry_id = this.company.industry.id;
                }
                const _logo = response.data.logo
                  ? (process.env.VUE_APP_IMAGE_URL + 'users/' + response.data.logo)
                  : null;
                useSEO({
                  title: response.data.name,
                  description: response.data.short_description,
                  image: _logo,
                  url: window.location.href,
                });
                this.project.user_id = response.data.userid.id;
                this.property.user_id = response.data.userid.id;
                this.plan.user_id = response.data.userid.id;
                this.postingAs = { id: response.data.userid.id, name: response.data.name, avatar: response.data.logo };
                this.checkCompanyFollower(this.company.id);
                this.totalCompanyFollower(this.company.id);
                this.fetchCompanyFollowers(this.company.id);
                this.fetchCurrentStates(this.company.country_id);
                this.fetchCurrentCities(this.company.state_id);
                this.fetchConnections(response.data.userid.id);
            }).catch({});
        },

        fetchConnections (id) {
            axios(process.env.VUE_APP_API_URL + 'user/projects/'+id).then(response =>{
                if (response.status !== 200) return;
                this.connections = response.data;
            }).catch(() => {});
        },

        fetchUserCompanies() {
          const userId = localStorage.getItem('user');
          if (!userId) return;
          axios(process.env.VUE_APP_API_URL + 'user/get-companies/' + userId)
            .then(res => {
              if (res.data && res.data.data) this.myCompanies = res.data.data;
            })
            .catch(() => {});
        },

        fetchCurrency () {
            axios(process.env.VUE_APP_API_URL + 'currency').then(response =>{
                if (response.status !== 200) return;
                this.currency = response.data;
            }).catch(() => {});
        },

        fetchIndustries () {
            axios(process.env.VUE_APP_API_URL + 'fetch-industries').then(response =>{
                if (response.data) this.industries = response.data;
            }).catch(() => {});
        },

        fetchCompanySetting () {
            axios(process.env.VUE_APP_API_URL + 'company').then(response =>{
                if (response.status !== 200) return;
                this.settings = response.data;
            }).catch(() => {});
        },

        checkCompanyFollower (business_id) {
            axios(process.env.VUE_APP_API_URL + 'user/check-company-follow/'+localStorage.getItem('user')+'/'+business_id).then(response =>{
                if (! response.status == 200) {
                    return this.errorMessage = 'Could not fetch data';
                }
                this.check_follower = response.data;
            }).catch({});
        },

        totalCompanyFollower (business_id) {
            axios(process.env.VUE_APP_API_URL + 'user/total-company-follower/'+business_id).then(response =>{
                if (! response.status == 200) {
                    return this.errorMessage = 'Could not fetch data';
                }
                this.total_follower = response.data;
            }).catch({});
        },

        fetchCompanyFollowers(business_id) {
            axios(process.env.VUE_APP_API_URL + 'user/company-followers/'+business_id).then(response =>{
                if (! response.status == 200) {
                    return this.errorMessage = 'Could not fetch data';
                }
                this.followers = response.data;
            }).catch({});
        },

        fetchCountries () {
            axios(process.env.VUE_APP_API_URL + 'country').then(response =>{
                if (! response.status == 200) {
                    return this.errorMessage = 'Could not fetch data';
                }
                this.country = response.data;
            }).catch({});
        },

        fetchStates (e) {
            axios(process.env.VUE_APP_API_URL + 'state/'+e.target.value).then(response =>{
                if (response.status !== 200) return;
                this.state = response.data;
            }).catch(() => {});
        },

        fetchCities (e) {
            axios(process.env.VUE_APP_API_URL + 'city/'+e.target.value).then(response =>{
                if (response.status !== 200) return;
                this.city = response.data;
            }).catch(() => {});
        },

        fetchCurrentStates () {
            axios(process.env.VUE_APP_API_URL + 'state/'+this.company.country_id).then(response =>{
                if (response.status !== 200) return;
                this.state = response.data;
            }).catch(() => {});
        },

        fetchCurrentCities () {
            axios(process.env.VUE_APP_API_URL + 'city/'+this.company.state_id).then(response =>{
                if (response.status !== 200) return;
                this.city = response.data;
            }).catch(() => {});
        },

        fetchNigeriaStates () {
            axios(process.env.VUE_APP_API_URL + 'state/'+160).then(response =>{
                if (! response.status == 200) {
                    return this.errorMessage = 'Could not fetch data';
                }
                this.state = response.data;
            }).catch({});
        },

        fetchLagosCities () {
            axios(process.env.VUE_APP_API_URL + 'city/'+2671).then(response =>{
                if (! response.status == 200) {
                    return this.errorMessage = 'Could not fetch data';
                }
                this.city = response.data;
            }).catch({});
        },

        fetchUsers () {
            axios(process.env.VUE_APP_API_URL + 'users').then(response =>{
                if (response.status !== 200) return;
                this.users = response.data;
            }).catch(() => {});
        },

        fetchVendorCategory () {
            axios(process.env.VUE_APP_API_URL + 'vendor-category').then(response =>{
                if (response.status !== 200) return;
                this.vendor_category = response.data;
            }).catch(() => {});
        },

        fectchInterests () {
            axios(process.env.VUE_APP_API_URL + 'project-interest').then(response =>{
                if (response.status !== 200) return;
                this.interests = response.data;
            }).catch(() => {});
        },

        fectchPropertyInterests () {
            axios(process.env.VUE_APP_API_URL + 'property-interest').then(response =>{
                if (response.status !== 200) return;
                this.property_interests = response.data;
            }).catch(() => {});
        },

        fectchPlanInterests () {
            axios(process.env.VUE_APP_API_URL + 'plan-interest').then(response =>{
                if (response.status !== 200) return;
                this.plan_interests = response.data;
            }).catch(() => {});
        },

        fectchInterestChildren (e) {
            this.interest_children = [];
            this.interest_children2 = [];
            axios(process.env.VUE_APP_API_URL + 'interest/'+e.target.value).then(response =>{
                if (response.status !== 200) return;
                this.interest_children = response.data;
            }).catch(() => {});
        },

        fectchInterestChildren2 (e) {
            this.interest_children2 = [];
            axios(process.env.VUE_APP_API_URL + 'interest/'+e.target.value).then(response =>{
                if (response.status !== 200) return;
                this.interest_children2 = response.data;
            }).catch(() => {});
        },

        onEditLogoChange(e) {
          const file = e.target.files[0];
          if (!file) return;
          this.editLogoFile = file;
          const reader = new FileReader();
          reader.onload = ev => { this.editLogoPreview = ev.target.result; };
          reader.readAsDataURL(file);
        },

        updateCompany () {
          this.loading = true;
          let payload;
          if (this.editLogoFile) {
            const fd = new FormData();
            Object.keys(this.company).forEach(k => {
              if (this.company[k] !== null && this.company[k] !== undefined) fd.append(k, this.company[k]);
            });
            fd.append('logo', this.editLogoFile);
            payload = fd;
          } else {
            payload = this.company;
          }
          axios.post(process.env.VUE_APP_API_URL + 'user/update-company', payload)
          .then((response) => {
            if (response.status == 200) {
              window.Fire.emit('refresh-action');
              this.$toastr.s('Company updated successfully.');
              this.loading = false;
              this.editLogoFile = null;
              this.editLogoPreview = '';
              this.$refs.ucCloseBtn?.click();
            } else {
              this.$toastr.e(response.data?.message || 'Update failed.');
              this.loading = false;
            }
          }).catch((error) =>{
            this.$toastr.e(error?.response?.data?.message || "Something went wrong.");
            this.loading = false;
          });
        },

        followCompany (id) {
            this.loading = true;
            axios.post(process.env.VUE_APP_API_URL + 'user/follow-company', {"business_id":id, "user_id":localStorage.getItem('user')})
            .then((response) => {
            if (response.status === 200) {
              this.check_follower = 1;
              this.total_follower = (parseInt(this.total_follower) || 0) + 1;
              this.$toastr.s(response.message);
              this.loading = false;
            } else {
                this.$toastr.e(response.data.message);
                this.loading = false;
            }
            }).catch((error) =>{
                this.$toastr.e(error?.response?.data?.message || "Something went wrong.");
                this.loading = false;
            })
        },

        unfollowCompany (id) {
            this.loading = true;
            axios.post(process.env.VUE_APP_API_URL + 'user/unfollow-company', {"business_id":id, "user_id":localStorage.getItem('user')})
            .then((response) => {
            if (response.status === 200) {
              this.check_follower = 0;
              this.total_follower = Math.max(0, (parseInt(this.total_follower) || 0) - 1);
              this.$toastr.s(response.message);
              this.loading = false;
            } else {
                this.$toastr.e(response.data.message);
                this.loading = false;
            }
            }).catch((error) =>{
                this.$toastr.e(error?.response?.data?.message || "Something went wrong.");
                this.loading = false;
            })
        },

        follow (id) {
            this.loading = true;
            axios.post(process.env.VUE_APP_API_URL + 'user/follow', {"user_id":id, "followed_by":localStorage.getItem('user')})
            .then((response) => {
            if (response.status === 200) {
                this.$toastr.s(response.message);
                this.loading = false;
            } else {
                this.$toastr.e(response.data.message);
                this.loading = false;
            }
            }).catch((error) =>{
                this.$toastr.e(error?.response?.data?.message || "Something went wrong.");
                this.loading = false;
            })
        },

        createProject () {
            const postUserId = this.postingAs ? this.postingAs.id : localStorage.getItem('user');
            this.loading = true;
            if(this.type == 'project'){
              axios.post(process.env.VUE_APP_API_URL + 'project/create', { ...this.project, user_id: postUserId })
              .then((response) => {
            if (response.status == 200) {
                this.$toastr.s(response.message);
                this.created = true;
                window.Fire.emit('refresh-project')
                this.type = '';
                this.images = [];
                this.project.name = '';
                this.project.description = '';
                this.project.project_start_date = '';
                this.project.project_end_date = '';
                this.project.country_id = '';
                this.project.state_id = '';
                this.project.city_id = '';
                this.project.status = '';
                this.project.height = '';
                this.project.address = '';
                this.project.architect = '';
                this.project.structural_engineer = '';
                this.project.mep_engineer = '';
                this.project.client = '';
                this.project.quantity_surveyor = '';
                this.project.main_contractor = '';
                this.project.mechanical_contractor = '';
                this.project.electrical_contractor = '';
                this.project.piling_contractor = '';
                this.project.lift_contractor = '';
                      this.project.project_manager = '';
                      this.project.architect_team = [];
                      this.project.structural_engineer_team = [];
                      this.project.mep_engineer_team = [];
                      this.project.quantity_surveyor_team = [];
                      this.project.main_contractor_team = [];
                      this.project.mechanical_contractor_team = [];
                      this.project.electrical_contractor_team = [];
                      this.project.piling_contractor_team = [];
                      this.project.lift_contractor_team = [];
                      this.project.project_manager_team = [];
                      this.project.project_vendors = [];
                this.project.youtube_url = '';
                this.project.instagram_url = '';
                this.project.images = '';
                this.loading = false;
            } else {
                this.$toastr.e(response.data.message);
                this.loading = false;
            }
            }).catch((error) =>{
                this.$toastr.e(error?.response?.data?.message || "Something went wrong.");
                this.loading = false;
            })

          } else if(this.type == 'property') {
             axios.post(process.env.VUE_APP_API_URL + 'property/create', { ...this.property, user_id: postUserId })
             .then((response) => {
            if (response.status == 200) {
                this.$toastr.s(response.message);
                this.created = true;
                window.Fire.emit('refresh-property')
                this.type = '';
                this.images = [];
                this.property.name = '';
                this.property.description = '';
                this.property.country_id = '';
                this.property.state_id = '';
                this.property.city_id = '';
                this.property.address = '';
                this.property.type = '';
                this.property.prices = [];
                this.property.details = [];
                this.property.youtube_url = '';
                this.property.instagram_url = '';
                this.property.images = '';
                this.loading = false;
            } else {
                this.$toastr.e(response.data.message);
                this.loading = false;
            }
            }).catch((error) =>{
                this.$toastr.e(error?.response?.data?.message || "Something went wrong.");
                this.loading = false;
            })
          } else if(this.type == 'plan') {

            axios.post(process.env.VUE_APP_API_URL + 'plan/create', { ...this.plan, user_id: postUserId })
            .then((response) => {
              if (response.status == 200) {
                  this.$toastr.s(response.message);
                  this.created = true;
                  window.Fire.emit('refresh-plans')
                  // this.fetchData();
                  this.type = '';
                  this.images = [];
                  this.plan.title = '';
                  this.plan.description = '';
                  this.plan.land_size = '';
                  this.plan.land_size_unit = '';
                  this.plan.height = '';
                  this.plan.youtube_url = '';
                  this.plan.instagram_url = '';
                  this.plan.images = '';
                  this.loading = false;
              } else {
                  this.$toastr.e(response.data.message);
                  this.loading = false;
              }
            }).catch((error) =>{
                this.$toastr.e(error?.response?.data?.message || "Something went wrong.");
                this.loading = false;
            })
          }
        },

        cancelForm(){
          if(confirm('Are you sure you want to discard this form?')){
            // this.$router.go(0);
            window.$('#project-modal').modal('hide');
            this.type = '';
            this.images = [];
            this.postingAs = this.company && this.company.userid ? { id: this.company.userid.id, name: this.company.name, avatar: this.company.logo } : null;
            this.project.name = '';
            this.project.description = '';
            this.project.project_start_date = '';
            this.project.project_end_date = '';
            this.project.country_id = '';
            this.project.state_id = '';
            this.project.city_id = '';
            this.project.status = '';
            this.project.height = '';
            this.project.address = '';
            this.project.architect = '';
            this.project.structural_engineer = '';
            this.project.mep_engineer = '';
            this.project.client = '';
            this.project.quantity_surveyor = '';
            this.project.main_contractor = '';
            this.project.mechanical_contractor = '';
            this.project.electrical_contractor = '';
            this.project.piling_contractor = '';
            this.project.lift_contractor = '';
            this.project.project_manager = '';
            this.project.architect_team = [];
            this.project.structural_engineer_team = [];
            this.project.mep_engineer_team = [];
            this.project.quantity_surveyor_team = [];
            this.project.main_contractor_team = [];
            this.project.mechanical_contractor_team = [];
            this.project.electrical_contractor_team = [];
            this.project.piling_contractor_team = [];
            this.project.lift_contractor_team = [];
            this.project.project_manager_team = [];
            this.project.project_vendors = [];
            this.project.youtube_url = '';
            this.project.instagram_url = '';
            this.project.images = '';
            this.property.name = '';
            this.property.description = '';
            this.property.country_id = '';
            this.property.state_id = '';
            this.property.city_id = '';
            this.property.address = '';
            this.property.type = '';
            this.property.prices = [];
            this.property.details = [];
            this.property.youtube_url = '';
            this.property.instagram_url = '';
            this.property.images = '';
            this.plan.title = '';
            this.plan.description = '';
            this.plan.land_size = '';
            this.plan.land_size_unit = '';
            this.plan.height = '';
            this.plan.youtube_url = '';
            this.plan.instagram_url = '';
            this.plan.images = '';
          }
        },

        closeForm(){
          this.created = false;
          this.$refs.lmCloseBtn?.click();
            this.type = '';           
            this.images = [];
            this.project.name = '';
            this.project.description = '';
            this.project.project_start_date = '';
            this.project.project_end_date = '';
            this.project.country_id = '';
            this.project.state_id = '';
            this.project.city_id = '';
            this.project.status = '';
            this.project.height = '';
            this.project.address = '';
            this.project.architect = '';
            this.project.structural_engineer = '';
            this.project.mep_engineer = '';
            this.project.client = '';
            this.project.quantity_surveyor = '';
            this.project.main_contractor = '';
            this.project.mechanical_contractor = '';
            this.project.electrical_contractor = '';
            this.project.piling_contractor = '';
            this.project.lift_contractor = '';
            this.project.project_manager = '';
            this.project.architect_team = [];
            this.project.structural_engineer_team = [];
            this.project.mep_engineer_team = [];
            this.project.quantity_surveyor_team = [];
            this.project.main_contractor_team = [];
            this.project.mechanical_contractor_team = [];
            this.project.electrical_contractor_team = [];
            this.project.piling_contractor_team = [];
            this.project.lift_contractor_team = [];
            this.project.project_manager_team = [];
            this.project.project_vendors = [];
            this.project.youtube_url = '';
            this.project.instagram_url = '';
            this.project.images = '';
            this.property.name = '';
            this.property.description = '';
            this.property.country_id = '';
            this.property.state_id = '';
            this.property.city_id = '';
            this.property.address = '';
            this.property.type = '';
            this.property.prices = [];
            this.property.details = [];
            this.property.youtube_url = '';
            this.property.instagram_url = '';
            this.property.images = '';
            this.plan.title = '';
            this.plan.description = '';
            this.plan.land_size = '';
            this.plan.land_size_unit = '';
            this.plan.height = '';
            this.plan.youtube_url = '';
            this.plan.instagram_url = '';
            this.plan.images = '';
        },

        submitComment(event, id) {
            this.loading = true;
            axios.post(process.env.VUE_APP_API_URL + 'project/comment', {"comment":event.target.value, "project_id":id, "user_id":localStorage.getItem('user')})
            .then((response) => {
            if (response.status === 200) {
                this.$toastr.s(response.message);
                this.comment.description = []
                window.Fire.emit('action')
                this.loading = false;
            } else {
                this.$toastr.e(response.data.message);
                this.loading = false;
            }
            }).catch((error) =>{
                this.$toastr.e(error?.response?.data?.message || "Something went wrong.");
                this.loading = false;
            })
        },

        submitPropertyComment(event, id) {
            this.loading = true;
            axios.post(process.env.VUE_APP_API_URL + 'property/comment', {"comment":event.target.value, "post_id":id, "user_id":localStorage.getItem('user')})
            .then((response) => {
            if (response.status === 200) {
                this.$toastr.s(response.message);
                this.comment.description = []
                window.Fire.emit('action')
                this.loading = false;
            } else {
                this.$toastr.e(response.data.message);
                this.loading = false;
            }
            }).catch((error) =>{
                this.$toastr.e(error?.response?.data?.message || "Something went wrong.");
                this.loading = false;
            })
        },

        likeTimeline(index, id){
          this.loading = true;
          axios.post(process.env.VUE_APP_API_URL + 'project/like', {"project_id":id, "user_id":localStorage.getItem('user')})
          .then((response) => {
          if (response.status === 200) {
              this.$toastr.s(response.message);
              window.Fire.emit('action')
          } else {
              this.$toastr.e(response.data.message);
              this.loading = false;
          }
          }).catch((error) =>{
              this.$toastr.e(error?.response?.data?.message || "Something went wrong.");
              this.loading = false;
          })
        },

        likePropertyTimeline(index, id){
          this.loading = true;
          axios.post(process.env.VUE_APP_API_URL + 'property/like', {"post_id":id, "user_id":localStorage.getItem('user')})
          .then((response) => {
          if (response.status === 200) {
              this.$toastr.s(response.message);
              window.Fire.emit('action')
          } else {
              this.$toastr.e(response.data.message);
              this.loading = false;
          }
          }).catch((error) =>{
              this.$toastr.e(error?.response?.data?.message || "Something went wrong.");
              this.loading = false;
          })
        },

        likePlanTimeline(index, id){
          this.loading = true;
          axios.post(process.env.VUE_APP_API_URL + 'plan/like', {"plan_id":id, "user_id":localStorage.getItem('user')})
          .then((response) => {
          if (response.status === 200) {
              this.$toastr.s(response.message);
              window.Fire.emit('action')
          } else {
              this.$toastr.e(response.data.message);
              this.loading = false;
          }
          }).catch((error) =>{
              this.$toastr.e(error?.response?.data?.message || "Something went wrong.");
              this.loading = false;
          })
        },

        setImage: function (e) {
            const file = e.target.files[0]
            if (!file.type.includes("image/")) {
              alert("Please select an image file")
              return
            }
            if (typeof FileReader === "function") {
              const reader = new FileReader()
              reader.onload = event => {
                this.imageSrc = event.target.result

                // Rebuild cropperjs with the updated source
                this.$refs.cropper.replace(event.target.result)
              }
              reader.readAsDataURL(file)
            } else {
              alert("Sorry, FileReader API not supported")
            }
          },

          cropImage() {
            // Get image data for post processing, e.g. upload or setting image src
            this.croppedImageSrc = this.$refs.cropper.getCroppedCanvas().toDataURL()
          },

          uploadImage (id) {
            var self = this;
            self.loading = true;
            this.$refs.cropper.getCroppedCanvas().toBlob(function (blob) {
              let formData = new FormData()
              // Add name for our image
              formData.append("name", "image-name-" + new Date().getTime())
              // Append image file
              formData.append("file", blob)
              formData.append("id", id)
            axios.post(process.env.VUE_APP_API_URL + 'business/banner-update', formData)
              .then((response) => {
              if (response.status === 200) {
                  self.$toastr.s(response.message);
                  self.loading = false;
                  window.Fire.emit('refresh-action');
                  window.$('#update-banner').modal('hide');
              } else {
                  self.$toastr.e(response.message);
                  self.loading = false;
              }
              }).catch((error) =>{
                  self.$toastr.e(error?.response?.message || "Something went wrong.");
                  self.loading = false;
              })
          })
        },

        addArchitectTeam() {
          this.project.architect_team.push({
            user_id: '',
          })
        },

        deleteArchitectTeam(index) {
            this.project.architect_team.splice(index,1)
        },

        addStructuralEngineerTeam() {
          this.project.structural_engineer_team.push({
            user_id: '',
          })
        },

        deleteStructuralEngineerTeam(index) {
            this.project.structural_engineer_team.splice(index,1)
        },

        addMepEngineerTeam() {
          this.project.mep_engineer_team.push({
            user_id: '',
          })
        },

        deleteMepEngineerTeam(index) {
            this.project.mep_engineer_team.splice(index,1)
        },

        addSurveyorTeam() {
          this.project.quantity_surveyor_team.push({
            user_id: '',
          })
        },

        deleteSurveyorTeam(index) {
            this.project.quantity_surveyor_team.splice(index,1)
        },

        addMainContractorTeam() {
          this.project.main_contractor_team.push({
            user_id: '',
          })
        },

        deleteMainContractorTeam(index) {
            this.project.main_contractor_team.splice(index,1)
        },

        addMechanicalContractorTeam() {
          this.project.mechanical_contractor_team.push({
            user_id: '',
          })
        },

        deleteMechanicalContractorTeam(index) {
            this.project.mechanical_contractor_team.splice(index,1)
        },

        addElectricalContractorTeam() {
          this.project.electrical_contractor_team.push({
            user_id: '',
          })
        },

        deleteElectricalContractorTeam(index) {
            this.project.electrical_contractor_team.splice(index,1)
        },

        addPilingTeam() {
          this.project.piling_contractor_team.push({
            user_id: '',
          })
        },

        deletePilingTeam(index) {
            this.project.piling_contractor_team.splice(index,1)
        },

        addLiftTeam() {
            this.project.lift_contractor_team.push({
              user_id: '',
            })
          },
  
          deleteLiftTeam(index) {
              this.project.lift_contractor_team.splice(index,1)
          },

          addPMTeam() {
            this.project.project_manager_team.push({
              user_id: '',
            })
          },
  
          deletePMTeam(index) {
              this.project.project_manager_team.splice(index,1)
          },

        addVendor() {
          this.project.project_vendors.push({
            vendor: '',
            user_id: '',
          })
        },

        deleteVendor(index) {
            this.project.project_vendors.splice(index,1)
        },

        uploadImageSuccess(formData, index, fileList) {
          this.project.images = fileList;

          // Upload image api
          // axios.post('http://your-url-upload', { data: formData }).then(response => {
          //
          // })
        },

        uploadImageProperty(formData, index, fileList) {
          this.property.images = fileList;
        },

        uploadImagePlan(formData, index, fileList) {
            this.plan.images = fileList;
          },

        addPrice() {
          this.property.prices.push({
            title: '',
            price: ''
          })
        },

        deletePrice(index) {
            this.property.prices.splice(index,1)
        },

        addDetail() {
          this.property.details.push({
            title: '',
            description: ''
          })
        },

        deleteDetail(index) {
            this.property.details.splice(index,1)
        },

        beforeRemove (index, done, fileList) {

          var r = confirm("remove image")
          if (r == true) {
            done()
          } else {
            ''
          }
        },

        editImage (formData, index, fileList) {

        },

        dataChange (data) {

        },

        viewTeam (index) {
          this.currentData = this.projects[index];
          window.$('#team-view').modal('show');
        },

        viewTeam2 (index) {
          this.currentData = this.connections[index].project;
          window.$('#team-view').modal('show');
        },

        formatDate(date){
          return moment(date).fromNow();
        },

        formatDate2(date) {
            return moment(date).format('DD MMMM, YYYY');
        },

        newline() {
          this.comment.description = `${this.comment.description}\n`;
        },

        viewLikes (index) {
          this.currentData = this.projects[index];
          window.$('#project-likes').modal('show');
        },

        viewComments (index) {
          this.currentData = this.projects[index];
          window.$('#project-comments').modal('show');
        },

        viewPropertyLikes (index) {
          this.currentData = this.properties[index];
          window.$('#post-likes').modal('show');
        },

        viewPropertyComments (index) {
          this.currentData = this.properties[index];
          window.$('#post-comments').modal('show');
        },

        viewPlanLikes (index) {
          this.currentData = this.plans[index];
          window.$('#plan-likes').modal('show');
        },

        viewPlanComments (index) {
          this.currentData = this.plans[index];
          window.$('#plan-comments').modal('show');
        },

        showMore(id) {
            this.readMore = { ...this.readMore, [id]: true };
        },
        showLess(id) {
            this.readMore = { ...this.readMore, [id]: false };
        },

        sendSubmitComment (id) {
          var str = document.getElementById("submitCommentButton"+id).value;
          this.submitComment2(str, id);
        },

        sendSubmitPropertyComment (id) {
          var str = document.getElementById("submitPropertyCommentButton"+id).value;
          this.submitPropertyComment2(str, id);
        },

        submitComment2(comment, id) {
            this.loading = true;
            axios.post(process.env.VUE_APP_API_URL + 'project/comment', {"comment":comment, "project_id":id, "user_id":localStorage.getItem('user')})
            .then((response) => {
            if (response.status === 200) {
                this.$toastr.s(response.message);
                this.comment.description = []
                window.Fire.emit('action')
                this.loading = false;
            } else {
                this.$toastr.e(response.data.message);
                this.loading = false;
            }
            }).catch((error) =>{
                this.$toastr.e(error?.response?.data?.message || "Something went wrong.");
                this.loading = false;
            })
        },

        submitPropertyComment2(comment, id) {
            this.loading = true;
            axios.post(process.env.VUE_APP_API_URL + 'property/comment', {"comment":comment, "post_id":id, "user_id":localStorage.getItem('user')})
            .then((response) => {
            if (response.status === 200) {
                this.$toastr.s(response.message);
                this.comment.description = []
                window.Fire.emit('action')
                this.loading = false;
            } else {
                this.$toastr.e(response.data.message);
                this.loading = false;
            }
            }).catch((error) =>{
                this.$toastr.e(error?.response?.data?.message || "Something went wrong.");
                this.loading = false;
            })
        },

      sendSubmitPlanComment (id) {
        var str = document.getElementById("submitPlanCommentButton"+id).value;
        this.submitPlanComment(str, id);
      },

      submitPlanComment(comment, id) {
          this.loading = true;
          axios.post(process.env.VUE_APP_API_URL + 'plan/comment', {"comment":comment, "plan_id":id, "user_id":localStorage.getItem('user')})
          .then((response) => {
          if (response.status === 200) {
              this.$toastr.s(response.message);
              this.comment.description = []
              window.Fire.emit('action')
              this.loading = false;
          } else {
              this.$toastr.e(response.data.message);
              this.loading = false;
          }
          }).catch((error) =>{
              this.$toastr.e(error?.response?.data?.message || "Something went wrong.");
              this.loading = false;
          })
      },

        onImgError(e) {
          e.target.src = '/assets/avatar.png';
        },

        onLogoError(e) {
          e.target.src = this.initAvatar(this.company.name);
        },
        nameColor(name) {
          const palette = ['#4f46e5','#0891b2','#059669','#d97706','#7c3aed','#db2777','#ea580c','#0284c7'];
          let h = 0;
          for (const c of (name || '')) h = (h * 31 + c.charCodeAt(0)) & 0xffffffff;
          return palette[Math.abs(h) % palette.length];
        },
        initAvatar(name) {
          const initials = (name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');
          const bg = this.nameColor(name);
          const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 80 80"><rect width="80" height="80" rx="12" fill="${bg}"/><text x="40" y="52" font-family="Arial,sans-serif" font-size="28" font-weight="700" fill="#fff" text-anchor="middle">${initials}</text></svg>`;
          return 'data:image/svg+xml;base64,' + btoa(svg);
        },

      confirmDelete(type, id, name) {
        this.cdType = type;
        this.cdId = id;
        this.cdName = name || 'this listing';
        this.cdVisible = true;
      },
      cdCancel() {
        this.cdVisible = false;
        this.cdType = '';
        this.cdId = null;
        this.cdName = '';
      },
      cdConfirm() {
        if (this.cdType === 'project') this.deleteProject(this.cdId);
        else if (this.cdType === 'property') this.deleteProperty(this.cdId);
        else if (this.cdType === 'plan') this.deletePlan(this.cdId);
      },

      deleteProject (id) {
        this.cdLoading = true;
        axios.delete(process.env.VUE_APP_API_URL + 'project/delete/'+id)
        .then((response) => {
          if (response.status === 200) {
            this.$toastr.s('Project deleted.');
            window.Fire.emit('refresh-project');
          } else {
            this.$toastr.e(response.data.message);
          }
        }).catch((error) =>{
          this.$toastr.e(error?.response?.data?.message || "Something went wrong.");
        }).finally(() => { this.cdLoading = false; this.cdCancel(); });
      },

      deleteProperty (id) {
        this.cdLoading = true;
        axios.delete(process.env.VUE_APP_API_URL + 'property/delete/'+id)
        .then((response) => {
          if (response.status === 200) {
            this.$toastr.s('Property deleted.');
            window.Fire.emit('refresh-property');
          } else {
            this.$toastr.e(response.data.message);
          }
        }).catch((error) =>{
          this.$toastr.e(error?.response?.data?.message || "Something went wrong.");
        }).finally(() => { this.cdLoading = false; this.cdCancel(); });
      },

      deletePlan (id) {
        this.cdLoading = true;
        axios.delete(process.env.VUE_APP_API_URL + 'plan/delete/'+id)
        .then((response) => {
          if (response.status === 200) {
            this.$toastr.s('Concept deleted.');
            window.Fire.emit('refresh-plans');
          } else {
            this.$toastr.e(response.data.message);
          }
        }).catch((error) =>{
          this.$toastr.e(error?.response?.data?.message || "Something went wrong.");
        }).finally(() => { this.cdLoading = false; this.cdCancel(); });
      },

    },

    mounted: function () {
      window.Fire.on('refresh-action', ()=>{
        this.fetchCompany()
      })

      window.Fire.on('refresh-project', ()=>{
        this.fetchProjects()
      })

      window.Fire.on('refresh-property', ()=>{
        this.fetchProperties()
      })

      window.Fire.on('refresh-plans', ()=>{
        this.fetchPlans()
      })

      window.Fire.on('action', ()=>{
        this.fetchProjects()
        this.fetchProperties()
        this.fetchPlans()
      })
    },

    computed: {
        image_url() {
          return process.env.VUE_APP_IMAGE_URL;
        },
        isOwner() {
          return this.company.user_id && this.company.user_id == this.id;
        },
        logoSrc() {
          if (this.company.logo) return this.image_url + 'users/' + this.company.logo;
          return this.initAvatar(this.company.name);
        },
        companyLocation() {
          const parts = [
            this.company.city ? this.company.city.name : '',
            this.company.state ? this.company.state.name : '',
            this.company.country ? this.company.country.name : '',
          ].filter(Boolean);
          return parts.join(', ');
        },
    },
}
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style scoped>
/* ── Banner ── */
.co-banner {
  position: relative;
  height: 220px;
  background: linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #0891b2 100%);
  overflow: hidden;
}
.co-banner__img {
  position: absolute; inset: 0;
  width: 100%; height: 100%;
  object-fit: cover;
}
.co-banner__dots {
  position: absolute; inset: 0;
  background-image: radial-gradient(circle, rgba(255,255,255,.08) 1px, transparent 1px);
  background-size: 28px 28px;
}
.co-banner__edit {
  position: absolute; top: 16px; right: 16px;
  background: rgba(0,0,0,.45); border: none; color: #fff;
  width: 36px; height: 36px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; font-size: 15px;
  transition: background .2s;
  z-index: 2;
}
.co-banner__edit:hover { background: rgba(0,0,0,.65); }

/* ── Container ── */
.sc-container { max-width: 1100px; margin: 0 auto; padding: 0 16px 48px; }

/* ── Header Card ── */
.co-header {
  display: flex; align-items: flex-end; gap: 20px;
  background: var(--surface, #fff);
  border: 1px solid var(--border-light, #e5e7eb);
  border-radius: var(--radius-lg, 12px);
  padding: 0 24px 20px;
  margin-top: -56px;
  position: relative; z-index: 1;
  box-shadow: var(--shadow, 0 1px 6px rgba(0,0,0,.08));
  flex-wrap: wrap;
}
.co-logo-wrap {
  margin-top: -36px;
  flex-shrink: 0;
}
.co-logo {
  width: 96px; height: 96px;
  border-radius: 12px;
  border: 3px solid #fff;
  object-fit: cover;
  box-shadow: 0 2px 8px rgba(0,0,0,.12);
  background: #f3f4f6;
}
.co-info { flex: 1; min-width: 0; padding-top: 16px; }
.co-name {
  font-size: 1.4rem; font-weight: 700;
  color: var(--text, #111827); margin: 0 0 4px;
  display: flex; align-items: center; gap: 6px;
}
.co-verified { flex-shrink: 0; }
.co-tagline { margin: 0 0 4px; color: var(--text-muted, #6b7280); font-size: .95rem; }
.co-location {
  display: flex; align-items: center; gap: 4px;
  color: var(--text-muted, #6b7280); font-size: .85rem; margin: 0;
}
.co-actions { display: flex; align-items: center; gap: 8px; padding-top: 16px; flex-wrap: wrap; }
.co-btn {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 16px; border-radius: 8px;
  font-size: .875rem; font-weight: 500; cursor: pointer;
  border: none; text-decoration: none; transition: all .18s;
}
.co-btn--primary { background: var(--primary, #2563eb); color: #fff; }
.co-btn--primary:hover { filter: brightness(1.1); }
.co-btn--outline {
  background: transparent;
  border: 1.5px solid var(--border-light, #e5e7eb);
  color: var(--text, #111827);
}
.co-btn--outline:hover { background: #f3f4f6; }
.co-btn--muted { opacity: .65; }
.co-btn--muted:hover { opacity: 1; }

/* ── Stats ── */
.co-stats {
  display: flex; align-items: center;
  background: var(--surface, #fff);
  border: 1px solid var(--border-light, #e5e7eb);
  border-radius: var(--radius-lg, 12px);
  padding: 16px 24px; margin-top: 12px;
  box-shadow: var(--shadow, 0 1px 6px rgba(0,0,0,.08));
  flex-wrap: wrap; gap: 8px;
}
.co-stat { display: flex; flex-direction: column; align-items: center; flex: 1; min-width: 60px; }
.co-stat__value { font-size: 1.25rem; font-weight: 700; color: var(--primary, #2563eb); }
.co-stat__label { font-size: .78rem; color: var(--text-muted, #6b7280); margin-top: 2px; }
.co-stat-divider { width: 1px; height: 32px; background: var(--border-light, #e5e7eb); }

/* ── Body Grid ── */
.co-body { display: grid; grid-template-columns: 280px 1fr; gap: 20px; margin-top: 20px; }
@media (max-width: 768px) { .co-body { grid-template-columns: 1fr; } }

/* ── Aside ── */
.aside-card {
  background: var(--surface, #fff);
  border: 1px solid var(--border-light, #e5e7eb);
  border-radius: var(--radius-lg, 12px);
  padding: 20px;
  margin-bottom: 16px;
  box-shadow: var(--shadow, 0 1px 6px rgba(0,0,0,.08));
}
.aside-title { font-size: .95rem; font-weight: 700; color: var(--text, #111827); margin: 0 0 12px; }
.aside-desc { font-size: .875rem; color: var(--text, #374151); line-height: 1.6; margin-bottom: 12px; }
.aside-empty { color: var(--text-muted, #6b7280); font-size: .875rem; font-style: italic; }
.aside-meta { list-style: none; padding: 0; margin: 12px 0 0; }
.aside-meta li {
  display: flex; align-items: center; gap: 8px;
  padding: 6px 0; font-size: .85rem; color: var(--text, #374151);
  border-bottom: 1px solid var(--border-light, #f3f4f6);
}
.aside-meta li:last-child { border-bottom: none; }
.aside-meta svg { color: var(--text-muted, #9ca3af); flex-shrink: 0; }
.aside-link { color: var(--primary, #2563eb); text-decoration: none; word-break: break-all; }
.aside-link:hover { text-decoration: underline; }

/* Follower widget */
.follower-item { display: flex; align-items: center; gap: 10px; padding: 8px 0; border-bottom: 1px solid var(--border-light, #f3f4f6); }
.follower-item:last-child { border-bottom: none; }
.follower-avatar img { width: 36px; height: 36px; border-radius: 50%; object-fit: cover; }
.follower-info { flex: 1; min-width: 0; text-decoration: none; }
.follower-name { display: block; font-size: .875rem; font-weight: 600; color: var(--text, #111827); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.follower-sub { display: block; font-size: .78rem; color: var(--text-muted, #6b7280); }

/* ── Filter Tabs ── */
.filter-tabs {
  display: flex; gap: 4px; flex-wrap: wrap;
  background: var(--surface, #fff);
  border: 1px solid var(--border-light, #e5e7eb);
  border-radius: var(--radius-lg, 12px);
  padding: 8px;
  margin-bottom: 16px;
  box-shadow: var(--shadow, 0 1px 6px rgba(0,0,0,.08));
}
.tab-btn {
  padding: 8px 14px; border-radius: 8px;
  border: none; background: transparent;
  font-size: .875rem; font-weight: 500;
  color: var(--text-muted, #6b7280); cursor: pointer;
  transition: all .18s; display: flex; align-items: center; gap: 6px;
}
.tab-btn:hover { background: #f3f4f6; color: var(--text, #111827); }
.tab-btn--active { background: var(--primary, #2563eb); color: #fff; }
.tab-btn--active:hover { background: var(--primary, #2563eb); filter: brightness(1.08); }
.tab-count {
  background: rgba(255,255,255,.25); border-radius: 10px;
  padding: 1px 7px; font-size: .78rem;
}
.tab-btn:not(.tab-btn--active) .tab-count {
  background: var(--border-light, #e5e7eb); color: var(--text-muted, #6b7280);
}

/* ── Feed Cards ── */
.feed-card {
  background: var(--surface, #fff);
  border: 1px solid var(--border-light, #e5e7eb);
  border-radius: var(--radius-lg, 12px);
  margin-bottom: 16px;
  overflow: hidden;
  box-shadow: var(--shadow, 0 1px 6px rgba(0,0,0,.08));
  position: relative;
}
.feed-card__actions { position: absolute; top: 12px; right: 12px; z-index: 2; }
.feed-card__menu { color: var(--text-muted, #6b7280); font-size: 1.2rem; padding: 4px 8px; }
.feed-card__header {
  display: flex; align-items: center; gap: 12px;
  padding: 16px 16px 0;
}
.feed-card__avatar { width: 40px; height: 40px; border-radius: 50%; object-fit: cover; flex-shrink: 0; }
.feed-card__author { font-weight: 600; font-size: .9rem; color: var(--text, #111827); }
.feed-card__meta { font-size: .78rem; color: var(--text-muted, #6b7280); }
.feed-card__time { margin-left: auto; font-size: .78rem; color: var(--text-muted, #6b7280); white-space: nowrap; }
.feed-card__body { padding: 12px 16px; }
.feed-card__title { font-size: 1rem; font-weight: 700; color: var(--text, #111827); text-decoration: none; display: block; margin-bottom: 4px; }
.feed-card__title:hover { color: var(--primary, #2563eb); }
.feed-card__location { font-size: .8rem; color: var(--text-muted, #6b7280); margin: 0 0 8px; display: flex; align-items: center; gap: 4px; }
.feed-card__desc { font-size: .875rem; color: var(--text, #374151); line-height: 1.6; }
.feed-card__readmore { color: var(--primary, #2563eb); cursor: pointer; }
.feed-card__img-wrap { margin-top: 12px; border-radius: 8px; overflow: hidden; }
.feed-card__img { width: 100%; max-height: 360px; object-fit: cover; display: block; }
.feed-card__connections {
  display: flex; align-items: center; gap: 8px;
  margin-top: 10px; padding: 8px 12px;
  background: #f9fafb; border-radius: 8px;
  font-size: .82rem; color: var(--text-muted, #6b7280);
}
.connection-avatars { display: flex; }
.connection-avatars img { width: 26px; height: 26px; border-radius: 50%; border: 2px solid #fff; margin-right: -8px; object-fit: cover; }
.feed-card__footer {
  display: flex; align-items: center; gap: 12px;
  padding: 10px 16px;
  border-top: 1px solid var(--border-light, #f3f4f6);
}
.feed-card__reaction {
  background: none; border: none; cursor: pointer;
  color: var(--text-muted, #6b7280); font-size: .85rem;
  display: flex; align-items: center; gap: 4px;
  padding: 4px 8px; border-radius: 6px; transition: background .15s;
}
.feed-card__reaction:hover { background: #f3f4f6; }
.feed-card__comment {
  display: flex; gap: 10px; padding: 8px 16px;
  border-top: 1px solid var(--border-light, #f3f4f6);
}
.feed-card__comment-avatar { width: 30px; height: 30px; border-radius: 50%; object-fit: cover; flex-shrink: 0; }
.feed-card__comment-body { flex: 1; font-size: .82rem; }
.feed-card__comment-body p { margin: 2px 0 0; color: var(--text, #374151); }
.feed-card__comment-author { font-weight: 600; color: var(--text, #111827); }
.feed-card__comment-time { float: right; color: var(--text-muted, #9ca3af); font-size: .75rem; }
.feed-card__compose {
  display: flex; align-items: center; gap: 8px;
  padding: 10px 16px;
  border-top: 1px solid var(--border-light, #f3f4f6);
}
.feed-card__compose textarea {
  flex: 1; border: 1px solid var(--border-light, #e5e7eb);
  border-radius: 20px; padding: 8px 14px;
  font-size: .875rem; resize: none;
  outline: none; font-family: inherit;
}
.feed-card__compose button {
  background: none; border: none; cursor: pointer;
  color: var(--primary, #2563eb); font-size: 1.1rem; padding: 4px;
}

/* ── People Grid (Followers Tab) ── */
.people-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 12px; }
.people-card {
  background: var(--surface, #fff);
  border: 1px solid var(--border-light, #e5e7eb);
  border-radius: var(--radius-lg, 12px);
  padding: 16px; text-align: center;
  box-shadow: var(--shadow, 0 1px 6px rgba(0,0,0,.08));
}
.people-card__avatar { width: 60px; height: 60px; border-radius: 50%; object-fit: cover; margin-bottom: 8px; }
.people-card__name { display: block; font-weight: 600; font-size: .9rem; color: var(--text, #111827); text-decoration: none; }
.people-card__name:hover { color: var(--primary, #2563eb); }
.people-card__sub { font-size: .78rem; color: var(--text-muted, #6b7280); margin: 2px 0 10px; }
.people-card__follow {
  background: var(--primary, #2563eb); color: #fff;
  border: none; border-radius: 6px; padding: 5px 14px;
  font-size: .82rem; cursor: pointer; transition: filter .18s;
}
.people-card__follow:hover { filter: brightness(1.1); }

/* ── Connections ── */
.connections-list { display: flex; flex-direction: column; gap: 12px; }
.connection-card {
  background: var(--surface, #fff);
  border: 1px solid var(--border-light, #e5e7eb);
  border-radius: var(--radius-lg, 12px);
  padding: 16px;
  box-shadow: var(--shadow, 0 1px 6px rgba(0,0,0,.08));
}
.connection-card__top { display: flex; gap: 12px; }
.connection-card__info { flex: 1; min-width: 0; }
.connection-card__name { font-weight: 700; font-size: 1rem; color: var(--text, #111827); text-decoration: none; }
.connection-card__name:hover { color: var(--primary, #2563eb); }
.connection-card__location { font-size: .82rem; color: var(--primary, #2563eb); margin: 2px 0; }
.connection-card__dates { font-size: .78rem; color: var(--text-muted, #6b7280); margin: 0; }
.connection-card__thumb { width: 80px; height: 60px; object-fit: cover; border-radius: 6px; flex-shrink: 0; }
.connection-card__desc { font-size: .875rem; color: var(--text, #374151); margin: 10px 0 0; }

/* ── Empty State ── */
.co-empty {
  text-align: center; padding: 48px 16px;
  color: var(--text-muted, #9ca3af);
}
.co-empty svg { margin-bottom: 12px; opacity: .4; }
.co-empty p { font-size: .95rem; margin: 0; }

/* ── Edit Company Modal ── */
.uc-dialog { max-width: 640px !important; width: 100% !important; margin: 1.5rem auto !important; }
.uc-content {
  border: none !important; border-radius: 14px !important;
  box-shadow: 0 20px 60px rgba(0,0,0,.18) !important;
  overflow: hidden;
}
.uc-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 20px 24px 18px; border-bottom: 1px solid #f0f0f0;
}
.uc-title { font-size: 1.125rem; font-weight: 700; color: #111827; margin: 0; }
.uc-close {
  width: 32px; height: 32px; border-radius: 8px; border: none;
  background: #f3f4f6; color: #6b7280; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: background .15s, color .15s; padding: 0;
}
.uc-close:hover { background: #e5e7eb; color: #111827; }
.uc-body { padding: 20px 24px; max-height: 68vh; overflow-y: auto; }
.uc-form { display: flex; flex-direction: column; gap: 16px; }
.uc-field { display: flex; flex-direction: column; gap: 5px; }
.uc-label { font-size: .8125rem; font-weight: 600; color: #374151; }
.uc-req { color: #ef4444; }
.uc-input {
  width: 100%; padding: 9px 12px;
  border: 1.5px solid #e5e7eb; border-radius: 8px;
  font-size: .875rem; color: #111827; background: #fff;
  outline: none; transition: border-color .18s;
  font-family: inherit; box-sizing: border-box;
}
.uc-input:focus { border-color: var(--primary, #1e3a5f); }
.uc-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.uc-row--3 { grid-template-columns: 1fr 1fr 1fr; }
.uc-section-label { font-size: .8125rem; font-weight: 700; color: #6b7280; text-transform: uppercase; letter-spacing: .05em; margin: 0; }
.uc-quill { border-radius: 8px; overflow: hidden; }
.uc-logo-row { display: flex; align-items: center; gap: 14px; }
.uc-logo-preview { width: 72px; height: 72px; border-radius: 10px; object-fit: cover; border: 1.5px solid #e5e7eb; flex-shrink: 0; }
.uc-logo-actions { display: flex; flex-direction: column; gap: 4px; }
.uc-upload-btn {
  display: inline-flex; align-items: center; padding: 7px 14px;
  border-radius: 7px; border: 1.5px solid #e5e7eb; background: #fff;
  font-size: .8125rem; font-weight: 600; color: #374151; cursor: pointer;
  transition: border-color .15s, background .15s;
}
.uc-upload-btn:hover { border-color: var(--primary, #1e3a5f); background: #f8fafc; }
.uc-file-hidden { display: none; }
.uc-hint { font-size: .75rem; color: #9ca3af; margin: 0; }
.uc-footer {
  display: flex; align-items: center; justify-content: flex-end; gap: 10px;
  padding: 16px 24px; border-top: 1px solid #f0f0f0;
}
.uc-btn {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 9px 20px; border-radius: 8px; font-size: .875rem;
  font-weight: 600; cursor: pointer; transition: all .18s; border: none;
}
.uc-btn--primary { background: var(--primary, #1e3a5f); color: #fff; }
.uc-btn--primary:hover:not(:disabled) { filter: brightness(1.1); }
.uc-btn--primary:disabled { opacity: .6; cursor: not-allowed; }
.uc-btn--outline { background: transparent; border: 1.5px solid #e5e7eb; color: #374151; }
.uc-btn--outline:hover { background: #f3f4f6; }
.uc-spinner {
  width: 15px; height: 15px; border-radius: 50%;
  border: 2px solid rgba(255,255,255,.4); border-top-color: #fff;
  animation: uc-spin .7s linear infinite; display: inline-block;
}
@keyframes uc-spin { to { transform: rotate(360deg); } }
@media (max-width: 580px) {
  .uc-row, .uc-row--3 { grid-template-columns: 1fr; }
  .uc-body { padding: 16px; }
  .uc-header, .uc-footer { padding: 16px; }
}

/* ── Post as selector ── */
.post-as-row {
  display: flex; align-items: center; gap: .75rem;
  padding: .625rem .875rem; background: #f9fafb; border-radius: 8px;
  border: 1px solid #e5e7eb; flex-wrap: wrap;
}
.post-as-label { font-size: .8125rem; font-weight: 600; color: #6b7280; white-space: nowrap; }
.post-as-options { display: flex; flex-wrap: wrap; gap: .4rem; }
.post-as-opt {
  display: flex; align-items: center; gap: .4rem;
  padding: .3rem .625rem .3rem .3rem; border-radius: 999px;
  border: 2px solid #e5e7eb; background: #fff;
  cursor: pointer; transition: border-color .15s, background .15s;
  font-size: .8125rem; font-weight: 500; color: #374151;
}
.post-as-opt:hover { border-color: #2563eb; }
.post-as-opt--active { border-color: #2563eb; background: #eff6ff; color: #2563eb; }
.post-as-avatar { width: 22px; height: 22px; border-radius: 50%; object-fit: cover; }

/* ── Listing Modal (lm-) ── */
.lm-dialog { max-width: 700px !important; width: 100% !important; margin: 1.5rem auto !important; }
.lm-content { border: none !important; border-radius: 14px !important; overflow: hidden !important; box-shadow: 0 8px 40px rgba(0,0,0,.18) !important; }
.lm-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 18px 24px 16px; border-bottom: 1px solid #e5e7eb; background: #fff;
}
.lm-title { font-size: 1.125rem; font-weight: 700; color: #111827; margin: 0; }
.lm-close {
  width: 32px; height: 32px; border-radius: 8px; border: none;
  background: transparent; display: flex; align-items: center; justify-content: center;
  color: #6b7280; cursor: pointer; transition: all .15s; padding: 0;
}
.lm-close:hover { background: #f3f4f6; color: #111827; }
.lm-body { padding: 20px 24px; max-height: 70vh; overflow-y: auto; background: #fff; }
.lm-footer {
  display: flex; align-items: center; justify-content: flex-end; gap: 8px;
  padding: 14px 24px; border-top: 1px solid #e5e7eb; background: #f9fafb;
}
/* Type cards */
.lm-type-cards { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 4px; }
.lm-type-card {
  display: flex; flex-direction: column; align-items: center; gap: 6px;
  padding: 14px 10px; border-radius: 10px;
  border: 1.5px solid #e5e7eb; background: #fff;
  cursor: pointer; transition: all .18s;
  font-size: .875rem; font-weight: 500; color: #374151;
}
.lm-type-card:hover { border-color: #2563eb; background: #eff6ff; }
.lm-type-card--active { border-color: #2563eb; background: #eff6ff; color: #2563eb; }
.lm-type-card svg { color: #6b7280; }
.lm-type-card--active svg { color: #2563eb; }
/* Section */
.lm-section { margin-bottom: 20px; }
.lm-section-header { margin-bottom: 16px; padding-bottom: 10px; border-bottom: 1px solid #f3f4f6; }
.lm-section-title { font-size: .95rem; font-weight: 700; color: #111827; margin: 0; }
.lm-section-label { font-size: .8125rem; font-weight: 700; color: #6b7280; text-transform: uppercase; letter-spacing: .05em; margin: 0 0 8px; }
/* Fields */
.lm-form { display: flex; flex-direction: column; gap: 16px; }
.lm-field { display: flex; flex-direction: column; gap: 5px; }
.lm-label { font-size: .8125rem; font-weight: 600; color: #374151; }
.lm-label-row { display: flex; align-items: center; justify-content: space-between; }
.lm-req { color: #ef4444; }
.lm-input {
  width: 100%; padding: 9px 12px;
  border: 1.5px solid #e5e7eb; border-radius: 8px;
  font-size: .875rem; background: #fff; color: #111827;
  outline: none; transition: border-color .18s;
  font-family: inherit; box-sizing: border-box;
}
.lm-input:focus { border-color: #2563eb; }
.lm-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.lm-row--3 { grid-template-columns: 1fr 1fr 1fr; }
.lm-category-row { display: flex; flex-wrap: wrap; gap: 10px; }
.lm-category-row .lm-input { flex: 1 1 140px; min-width: 0; }
@media (max-width: 600px) {
  .lm-row, .lm-row--3 { grid-template-columns: 1fr; }
  .lm-type-cards { grid-template-columns: 1fr 1fr 1fr; }
  .lm-body { padding: 16px; }
  .lm-header, .lm-footer { padding: 14px 16px; }
}
/* Team */
.lm-add-link { font-size: .8rem; color: #2563eb; background: none; border: none; cursor: pointer; padding: 0; font-weight: 500; }
.lm-add-link:hover { text-decoration: underline; }
.lm-team-row { display: flex; align-items: center; gap: 8px; margin-top: 8px; padding: 8px 10px; background: #f9fafb; border-radius: 8px; }
.lm-team-row > *:first-child { flex: 1; min-width: 0; }
.lm-team-del { flex: 0 0 auto; background: none; border: none; color: #ef4444; cursor: pointer; font-size: 1.1rem; padding: 2px 6px; }
.lm-vendor-row { display: grid; grid-template-columns: 1fr 1fr auto; gap: 8px; margin-top: 8px; padding: 8px 10px; background: #f9fafb; border-radius: 8px; }
.lm-price-row { display: grid; grid-template-columns: 1fr 1fr auto; gap: 8px; margin-top: 8px; align-items: center; }
/* Radio / checkbox */
.lm-radio-group { display: flex; gap: 16px; flex-wrap: wrap; }
.lm-radio { display: flex; align-items: center; gap: 4px; font-size: .875rem; color: #374151; cursor: pointer; }
.lm-radio input { accent-color: #2563eb; }
/* Quill */
.lm-quill { border-radius: 8px; overflow: hidden; }
.lm-quill .ql-container { max-height: 160px; overflow-y: auto; }
/* Buttons */
.lm-btn {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 9px 20px; border-radius: 8px;
  font-size: .875rem; font-weight: 600; cursor: pointer;
  border: none; transition: all .18s;
}
.lm-btn--primary { background: var(--primary, #1e3a5f); color: #fff; }
.lm-btn--primary:hover:not(:disabled) { filter: brightness(1.1); }
.lm-btn--primary:disabled { opacity: .6; cursor: not-allowed; }
.lm-btn--outline { background: transparent; border: 1.5px solid #e5e7eb; color: #374151; }
.lm-btn--outline:hover { background: #f3f4f6; }
.lm-spinner {
  width: 15px; height: 15px; border-radius: 50%;
  border: 2px solid rgba(255,255,255,.3); border-top-color: #fff;
  animation: lm-spin .7s linear infinite; display: inline-block;
}
@keyframes lm-spin { to { transform: rotate(360deg); } }
/* Success */
.lm-success { text-align: center; padding: 40px 0; display: flex; flex-direction: column; align-items: center; gap: 12px; }
.lm-success__icon { display: block; }
.lm-success h3 { font-size: 1.25rem; font-weight: 700; color: #111827; margin: 0; }
.lm-success p { color: #6b7280; margin: 0; }

/* ── Confirm Delete Dialog (cd-) ── */
.cd-backdrop {
  position: fixed; inset: 0; z-index: 9999;
  background: rgba(0,0,0,.45);
  display: flex; align-items: center; justify-content: center;
  padding: 16px;
}
.cd-dialog {
  background: #fff; border-radius: 14px;
  padding: 32px 28px 24px;
  max-width: 380px; width: 100%;
  box-shadow: 0 12px 48px rgba(0,0,0,.22);
  text-align: center;
}
.cd-icon {
  width: 56px; height: 56px; border-radius: 50%;
  background: #fef2f2; color: #ef4444;
  display: flex; align-items: center; justify-content: center;
  margin: 0 auto 16px;
}
.cd-title { font-size: 1.125rem; font-weight: 700; color: #111827; margin: 0 0 8px; }
.cd-msg { font-size: .875rem; color: #6b7280; margin: 0 0 24px; line-height: 1.5; }
.cd-msg strong { color: #111827; }
.cd-actions { display: flex; gap: 10px; justify-content: center; }
.cd-btn {
  flex: 1; padding: 10px 0; border-radius: 8px;
  font-size: .875rem; font-weight: 600; cursor: pointer;
  border: none; transition: all .18s;
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
}
.cd-btn--cancel { background: #f3f4f6; color: #374151; }
.cd-btn--cancel:hover { background: #e5e7eb; }
.cd-btn--confirm { background: #ef4444; color: #fff; }
.cd-btn--confirm:hover:not(:disabled) { background: #dc2626; }
.cd-btn--confirm:disabled { opacity: .65; cursor: not-allowed; }
.cd-spinner {
  width: 14px; height: 14px; border-radius: 50%;
  border: 2px solid rgba(255,255,255,.4); border-top-color: #fff;
  animation: lm-spin .7s linear infinite;
}
.cd-fade-enter-active, .cd-fade-leave-active { transition: opacity .18s ease; }
.cd-fade-enter-from, .cd-fade-leave-to { opacity: 0; }
</style>